import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Database, Play, Eye, RotateCcw, AlertTriangle, CheckCircle, ArrowLeft, Loader2, Stethoscope, Trash2, Search, Wrench, PlayCircle, KeyRound, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const MIGRATIONS = [
  {
    id: 'basic-initiated',
    title: 'Basic Initiated Status Migration',
    description: 'Moves cases with "Process Basic Verification First" from pending_qc to basic_initiated status when the employee has not fully submitted.',
    criteria: 'status in [pending_qc, in_progress, qc_approved], basic_only_package≠true, client has Basic First enabled'
  },
  {
    id: 'bulk-import-fix',
    title: 'Bulk Import Employee Form Fix',
    description: 'Resets details_filled and declaration_signed to False for bulk-imported employees who received invitations but have not submitted, so they can fill their form.',
    criteria: 'source=bulk_import, invitation_sent_at exists, employee_submitted≠true, details_filled=true'
  },
  {
    id: 'declaration-docs',
    title: 'Declaration Document Backfill',
    description: 'Adds signed candidate declaration PDFs to the Documents collection so they appear in the Document Preview panel. Only processes employees who have a declaration_form_url but no matching document record.',
    criteria: 'declaration_form_url exists on employee, no matching document_type=declaration_form in documents collection'
  },
  {
    id: 'link-case-ids',
    title: 'Link Missing Case IDs',
    description: 'Finds employees with case_id=None and links them to their matching verification case (by employee_id). Also identifies orphaned employees with no case at all.',
    criteria: 'case_id is null/empty on employee, matching case exists in verification_cases by employee_id'
  },
  {
    id: 'clean-orphaned-employees',
    title: 'Clean Orphaned Employees',
    description: 'Finds and PERMANENTLY DELETES employees that have no matching verification case. Also removes their login credentials and uploaded documents. Use before re-importing data to prevent duplicates.',
    criteria: 'case_id is null/empty/invalid (not matching any real case) AND no matching case exists in verification_cases by employee_id'
  },
  {
    id: 'heal-agent-assignment',
    title: 'Heal Agent Ownership After Vendor Overwrite',
    description: 'Restores visibility of checks to their original agent (e.g. Agent Vendor Team member) after a subsequent vendor assignment overwrote assigned_to. Back-fills agent_assigned_to / agent_assigned_to_email / agent_assigned_to_name by looking up the preserved assigned_to_name in the users table. Safe: idempotent, skips name collisions (surfaced in the preview), never touches vendor fields.',
    criteria: 'Check has a vendor attached (vendor_id or assigned_vendor.vendor_id) AND agent_assigned_to is empty AND assigned_to_name resolves unambiguously to one user',
    supportsPrefix: true,
    prefixPlaceholder: 'e.g. INC210426_ (leave blank for all clients)'
  }
];

export default function AdminMigrationsPage() {
  const navigate = useNavigate();
  const [previews, setPreviews] = useState({});
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});
  const [diagnostic, setDiagnostic] = useState(null);
  const [selectedOrphans, setSelectedOrphans] = useState(new Set());
  const [selectedMigrationCases, setSelectedMigrationCases] = useState(new Set());
  const [entityFilter, setEntityFilter] = useState('all');
  
  // Case Diagnostic state
  const [diagCaseIds, setDiagCaseIds] = useState('');
  const [diagResults, setDiagResults] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [repairLoading, setRepairLoading] = useState(false);

  const [migrationPrefixes, setMigrationPrefixes] = useState({});
  // May 24, 2026 — defaults the Basic Initiated migration tool to show
  // only form-pending + in_progress cases. Admin can untick to see
  // everything (legacy view).
  const [onlyFormPending, setOnlyFormPending] = useState(true);

  // Agent Visibility Diagnostic (heal-agent-assignment card)
  const [visEmail, setVisEmail] = useState('');
  const [visPrefix, setVisPrefix] = useState('');
  const [visLoading, setVisLoading] = useState(false);
  const [visResult, setVisResult] = useState(null);

  // Query-profile diagnostic + index-apply
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfApplyLoading, setPerfApplyLoading] = useState(false);
  const [perfResult, setPerfResult] = useState(null);
  const [perfApplyResult, setPerfApplyResult] = useState(null);

  // Mirror Backfill (May 21, 2026) — heal historical bulk-imported
  // candidates whose present and permanent addresses match but
  // permanent_same_as_present was never set.
  const [mirrorScanLoading, setMirrorScanLoading] = useState(false);
  const [mirrorHealLoading, setMirrorHealLoading] = useState(false);
  const [mirrorScan, setMirrorScan] = useState(null);
  const [mirrorHealResult, setMirrorHealResult] = useState(null);
  const [mirrorOnlyBulk, setMirrorOnlyBulk] = useState(true);
  const [mirrorSelected, setMirrorSelected] = useState(new Set());

  // Reset Single Check (May 23, 2026) — for cases where return-to-ops
  // manager is stuck (e.g., VER300426_01005). Clears ALL state on one
  // check (vendor, agent, verification form, documents, completion, TAT,
  // remarks) so Ops Manager can re-assign it without disturbing other
  // verified work on the case.
  const [rcaseInput, setRcaseInput] = useState('');
  const [rcaseLoading, setRcaseLoading] = useState(false);
  const [rcaseSummary, setRcaseSummary] = useState(null);
  const [rcaseConfirm, setRcaseConfirm] = useState(null); // { check, preview }
  const [rcaseResetting, setRcaseResetting] = useState(false);

  // Remove Check (May 23, 2026) — destructive variant of Reset for
  // orphaned/stuck checks or client-requested mid-case drops. Archives
  // the removed check to case.removed_checks[] for forensic recovery.
  const [removeConfirm, setRemoveConfirm] = useState(null); // { check, preview }
  const [removePhrase, setRemovePhrase] = useState('');
  const [removeReason, setRemoveReason] = useState('');
  const [removeOverride, setRemoveOverride] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Repair Case Rollup (May 24, 2026) — fixes cases stuck at "Draft"/
  // report_pending_qc on the QC dashboard despite all checks being
  // verified. Re-runs the canonical status rollup helper and optionally
  // re-sends the completion-report email with the corrected subject.
  const [repairCaseId, setRepairCaseId] = useState('');
  const [repairRollupLoading, setRepairRollupLoading] = useState(false);
  const [repairPreview, setRepairPreview] = useState(null);
  const [repairResendEmail, setRepairResendEmail] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  // Address Sub-check Mismatch Repair (May 30, 2026) — scans for
  // address_check rows whose subcheck_data disagrees with sub_option
  // (present/permanent). Caused by a legacy bulk-import bug. Lets
  // admin select-and-repair without touching rows manually fixed
  // already.
  const [addrMismatchLoading, setAddrMismatchLoading] = useState(false);
  const [addrMismatchFindings, setAddrMismatchFindings] = useState(null);
  const [addrMismatchSelected, setAddrMismatchSelected] = useState({}); // {key: true}
  const [addrMismatchMode, setAddrMismatchMode] = useState('rewrite_to_canonical');
  const [addrMismatchIncludeCompleted, setAddrMismatchIncludeCompleted] = useState(true);
  const [addrMismatchRepairLoading, setAddrMismatchRepairLoading] = useState(false);
  const [addrMismatchResult, setAddrMismatchResult] = useState(null);
  // Per-case diagnostic (May 31, 2026)
  const [diagCaseId, setDiagCaseId] = useState('');
  const [diagCheckId, setDiagCheckId] = useState('');
  const [addrDiagLoading, setAddrDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  const previewCaseRollupRepair = async () => {
    const cid = repairCaseId.trim();
    if (!cid) { toast.error('Enter a Case ID'); return; }
    setRepairRollupLoading(true);
    setRepairPreview(null);
    setRepairResult(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/repair-case-rollup`,
        { case_id: cid, dry_run: true, re_send_report_email: repairResendEmail },
        getAuthHeader()
      );
      setRepairPreview(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Preview failed');
    } finally {
      setRepairRollupLoading(false);
    }
  };

  const applyCaseRollupRepair = async () => {
    const cid = repairCaseId.trim();
    if (!cid) return;
    setRepairRollupLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/repair-case-rollup`,
        { case_id: cid, dry_run: false, re_send_report_email: repairResendEmail, use_last_check_date: true },
        getAuthHeader()
      );
      setRepairResult(res.data);
      setRepairPreview(null);
      toast.success(res.data.message || 'Rollup repaired');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Repair failed');
    } finally {
      setRepairRollupLoading(false);
    }
  };

  // Revert Wrongly-Migrated Cases (May 24, 2026)
  //   Targeted cleanup for cases incorrectly moved to basic_initiated
  //   by the Basic Initiated migration tool. Restores them to their
  //   pre-migration status (typically in_progress) when the case
  //   shows post-submission signals (employee_submitted=True, an open
  //   insufficiency, etc.).
  const [revertWrongLoading, setRevertWrongLoading] = useState(false);
  const [revertWrongPreview, setRevertWrongPreview] = useState(null);
  const [revertWrongResult, setRevertWrongResult] = useState(null);

  const previewRevertWrong = async () => {
    setRevertWrongLoading(true);
    setRevertWrongResult(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/migrations/basic-initiated/revert-wrong/preview`,
        getAuthHeader()
      );
      setRevertWrongPreview(res.data);
      toast.success(`Found ${res.data.count} wrongly-migrated cases`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setRevertWrongLoading(false);
    }
  };

  const executeRevertWrong = async () => {
    if (!window.confirm(`Revert ${revertWrongPreview?.count || 0} cases back to their original status? This action will be logged.`)) return;
    setRevertWrongLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/basic-initiated/revert-wrong/execute`,
        {},
        getAuthHeader()
      );
      setRevertWrongResult(res.data);
      setRevertWrongPreview(null);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revert failed');
    } finally {
      setRevertWrongLoading(false);
    }
  };

  // ----- Address Sub-check Mismatch Repair (May 30, 2026) -----
  const scanAddrMismatch = async () => {
    setAddrMismatchLoading(true);
    setAddrMismatchResult(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/migrations/address-subcheck-mismatch/scan`,
        {
          ...getAuthHeader(),
          params: { include_completed: addrMismatchIncludeCompleted, limit: 1000 },
        }
      );
      setAddrMismatchFindings(res.data);
      setAddrMismatchSelected({});
      toast.success(`Found ${res.data.total_found} mismatched address check(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setAddrMismatchLoading(false);
    }
  };

  const repairSelectedAddrMismatch = async () => {
    const findings = addrMismatchFindings?.findings || [];
    const targets = findings
      .filter((f) => addrMismatchSelected[`${f.case_id}::${f.check_id}`])
      .map((f) => ({ case_id: f.case_id, check_id: f.check_id }));
    if (targets.length === 0) {
      toast.error('Select at least one check to repair');
      return;
    }
    if (!window.confirm(
      `Repair ${targets.length} check(s) using mode "${addrMismatchMode}"? ` +
      `This will ${addrMismatchMode === 'clear' ? 'WIPE subcheck_data' : 'rewrite subcheck_data to match the canonical address'} for the selected rows. An audit log entry is recorded on each.`
    )) return;
    setAddrMismatchRepairLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/address-subcheck-mismatch/repair`,
        { targets, mode: addrMismatchMode },
        getAuthHeader()
      );
      setAddrMismatchResult(res.data);
      toast.success(`Repaired ${res.data.repaired} / ${res.data.total_targets} (skipped: ${res.data.skipped}, failed: ${res.data.failed})`);
      // Re-scan so the table reflects the new state
      scanAddrMismatch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Repair failed');
    } finally {
      setAddrMismatchRepairLoading(false);
    }
  };

  const toggleAddrMismatchAll = () => {
    const findings = addrMismatchFindings?.findings || [];
    const allSelected = findings.every((f) => addrMismatchSelected[`${f.case_id}::${f.check_id}`]);
    if (allSelected) {
      setAddrMismatchSelected({});
    } else {
      const next = {};
      findings.forEach((f) => { next[`${f.case_id}::${f.check_id}`] = true; });
      setAddrMismatchSelected(next);
    }
  };

  const runAddrDiagnostic = async () => {
    if (!diagCaseId.trim()) {
      toast.error('Enter a case ID');
      return;
    }
    setAddrDiagLoading(true);
    setDiagResult(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/migrations/address-subcheck-diagnose`,
        {
          ...getAuthHeader(),
          params: {
            case_id: diagCaseId.trim(),
            ...(diagCheckId.trim() ? { check_id: diagCheckId.trim() } : {}),
          },
        }
      );
      setDiagResult(res.data);
      toast.success(`Diagnostic loaded: ${res.data.rows_returned} address check(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Diagnostic failed');
    } finally {
      setAddrDiagLoading(false);
    }
  };



  const loadCaseChecksForReset = async () => {
    if (!rcaseInput.trim()) { toast.error('Enter a Case ID'); return; }
    setRcaseLoading(true);
    setRcaseSummary(null);
    setRcaseConfirm(null);
    try {
      const res = await axios.get(`${API}/api/admin/migrations/case/${encodeURIComponent(rcaseInput.trim())}/checks-summary`, getAuthHeader());
      setRcaseSummary(res.data);
      toast.success(`${res.data.check_count} check(s) loaded for ${res.data.case_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load case checks');
    } finally {
      setRcaseLoading(false);
    }
  };

  const previewCheckReset = async (check) => {
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/reset-check`,
        {
          case_id: rcaseSummary.case_id,
          check_id: check.check_id,
          dry_run: true,
          reason: 'Admin manual reset via Migrations panel',
        },
        getAuthHeader()
      );
      setRcaseConfirm({ check, preview: res.data.preview });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dry-run failed');
    }
  };

  const applyCheckReset = async () => {
    if (!rcaseConfirm) return;
    setRcaseResetting(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/reset-check`,
        {
          case_id: rcaseSummary.case_id,
          check_id: rcaseConfirm.check.check_id,
          dry_run: false,
          reason: 'Admin manual reset via Migrations panel',
        },
        getAuthHeader()
      );
      toast.success(res.data.message);
      setRcaseConfirm(null);
      await loadCaseChecksForReset(); // refresh
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setRcaseResetting(false);
    }
  };

  // ── Remove Check handlers (May 23, 2026) ─────────────────────────
  const previewCheckRemove = async (check) => {
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/remove-check`,
        {
          case_id: rcaseSummary.case_id,
          check_id: check.check_id,
          reason: 'Preview',
          confirm_phrase: 'REMOVE',
          override_verified_safety: false, // dry-run still works without override; the apply will block if needed
          dry_run: true,
        },
        getAuthHeader()
      );
      setRemoveConfirm({ check, preview: res.data.preview });
      setRemovePhrase('');
      setRemoveReason('');
      setRemoveOverride(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Block-state dry-runs come back from the server with the
      // verified_safety_block code — surface as inline error in the
      // confirm panel rather than just a toast.
      if (detail && typeof detail === 'object' && detail.code === 'verified_safety_block') {
        setRemoveConfirm({ check, preview: { ...detail, blocked: true } });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Dry-run failed');
      }
    }
  };

  const applyCheckRemove = async () => {
    if (!removeConfirm) return;
    if ((removePhrase || '').trim().toUpperCase() !== 'REMOVE') {
      toast.error('Type REMOVE to confirm');
      return;
    }
    if (!(removeReason || '').trim()) {
      toast.error('Reason is required');
      return;
    }
    if (removeConfirm.preview?.is_protected_state && !removeOverride) {
      toast.error('Toggle the Override Safety switch to remove a verified check');
      return;
    }
    setRemoving(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/remove-check`,
        {
          case_id: rcaseSummary.case_id,
          check_id: removeConfirm.check.check_id,
          reason: removeReason,
          confirm_phrase: 'REMOVE',
          override_verified_safety: removeOverride,
          dry_run: false,
        },
        getAuthHeader()
      );
      toast.success(res.data.message);
      setRemoveConfirm(null);
      setRemovePhrase('');
      setRemoveReason('');
      setRemoveOverride(false);
      await loadCaseChecksForReset();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Remove failed');
      toast.error(msg);
    } finally {
      setRemoving(false);
    }
  };

  const runMirrorScan = async () => {
    setMirrorScanLoading(true);
    setMirrorHealResult(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/migrations/mirror-backfill/scan?limit=500&only_bulk_import=${mirrorOnlyBulk}`,
        getAuthHeader()
      );
      setMirrorScan(res.data);
      setMirrorSelected(new Set((res.data.candidates || []).map((c) => c.employee_id)));
      toast.success(`Scan complete — ${res.data.match_count} candidate(s) eligible`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mirror scan failed');
    } finally {
      setMirrorScanLoading(false);
    }
  };

  const runMirrorHeal = async (dryRun) => {
    setMirrorHealLoading(true);
    try {
      const ids = Array.from(mirrorSelected);
      const res = await axios.post(
        `${API}/api/admin/migrations/mirror-backfill/heal`,
        { employee_ids: ids, only_bulk_import: mirrorOnlyBulk, dry_run: dryRun },
        getAuthHeader()
      );
      setMirrorHealResult(res.data);
      toast.success(
        dryRun
          ? `Dry run — would heal ${res.data.would_heal_count} candidate(s)`
          : `Healed ${res.data.healed_count} candidate(s)`
      );
      if (!dryRun) {
        await runMirrorScan();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mirror heal failed');
    } finally {
      setMirrorHealLoading(false);
    }
  };

  const toggleMirrorSelected = (empId) => {
    setMirrorSelected((prev) => {
      const next = new Set(prev);
      if (next.has(empId)) next.delete(empId); else next.add(empId);
      return next;
    });
  };

  const runQueryProfile = async () => {
    setPerfLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/migrations/query-profile`, getAuthHeader());
      setPerfResult(res.data);
      toast.success('Query profile loaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Profile failed');
    } finally {
      setPerfLoading(false);
    }
  };

  const applyRecommendedIndexes = async () => {
    if (!window.confirm('Create recommended MongoDB indexes? Safe & idempotent — skips ones that already exist. May take 10-60s on large collections.')) return;
    setPerfApplyLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/migrations/query-profile/apply-indexes`, {}, getAuthHeader());
      setPerfApplyResult(res.data);
      toast.success(res.data.message);
      // Re-run profile to show new index_used values
      runQueryProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setPerfApplyLoading(false);
    }
  };

  const runAgentVisibilityDiag = async () => {
    if (!visEmail.trim()) { toast.error('Enter an agent email'); return; }
    setVisLoading(true);
    try {
      const qs = new URLSearchParams({ user_email: visEmail.trim() });
      if (visPrefix.trim()) qs.append('case_prefix', visPrefix.trim());
      // Call both diagnostic endpoints in parallel and merge — the first
      // reports OLD-vs-NEW filter visibility, the second adds case-status
      // breakdown and assigned_check_types drop stats.
      const [res1, res2] = await Promise.all([
        axios.get(`${API}/api/admin/migrations/debug-agent-visibility?${qs.toString()}`, getAuthHeader()),
        axios.get(`${API}/api/admin/migrations/debug-agent-team-checks?${qs.toString()}`, getAuthHeader()),
      ]);
      setVisResult({ ...res1.data, ...res2.data, user: res1.data.user || res2.data.user });
      toast.success(`Diagnostic loaded for ${res1.data.user?.name || visEmail}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Diagnostic failed');
    } finally {
      setVisLoading(false);
    }
  };

  // Case Forensics & Recovery state
  const [forensicsCaseId, setForensicsCaseId] = useState('');
  const [forensicsLoading, setForensicsLoading] = useState(false);
  const [forensicsResult, setForensicsResult] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [restorePreviewLoading, setRestorePreviewLoading] = useState(false);
  const [restoreExecuteLoading, setRestoreExecuteLoading] = useState(false);
  const [restoreEntity, setRestoreEntity] = useState(true);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const runForensics = async () => {
    const cid = forensicsCaseId.trim();
    if (!cid) { toast.error('Enter a Case ID'); return; }
    setForensicsLoading(true);
    setForensicsResult(null);
    setRestorePreview(null);
    try {
      const res = await axios.get(`${API}/api/admin/migrations/case-forensics/${encodeURIComponent(cid)}`, getAuthHeader());
      setForensicsResult(res.data);
      const anomalies = res.data?.anomalies?.length || 0;
      toast.success(`Forensics: ${res.data?.timeline_count || 0} timeline events, ${anomalies} anomaly(ies)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Forensics failed');
    } finally {
      setForensicsLoading(false);
    }
  };

  const runRestorePreview = async () => {
    const cid = forensicsCaseId.trim();
    if (!cid) { toast.error('Enter a Case ID'); return; }
    setRestorePreviewLoading(true);
    setRestorePreview(null);
    try {
      const res = await axios.get(`${API}/api/admin/migrations/case-restore-preview/${encodeURIComponent(cid)}`, getAuthHeader());
      setRestorePreview(res.data);
      if (res.data?.would_change) {
        toast.success('Restore preview ready — review before executing');
      } else {
        toast.info('No changes proposed — current linkage already matches the email-log snapshot');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore preview failed');
    } finally {
      setRestorePreviewLoading(false);
    }
  };

  const runRestoreExecute = async () => {
    const cid = forensicsCaseId.trim();
    if (!cid || !restorePreview?.proposed_employee) { toast.error('Run preview first'); return; }
    const proposed = restorePreview.proposed_employee;
    const cur = restorePreview.current_employee;
    if (!window.confirm(
      `Restore case ${cid}?\n\n` +
      `FROM: ${cur.employee_name} (${cur.employee_id})\n` +
      `TO:   ${proposed.employee_name} (${proposed.employee_id})\n\n` +
      `Entity will ${restoreEntity ? 'ALSO' : 'NOT'} be updated.\n\n` +
      `Previous linkage will be backed up under _corruption_backup.\nThis is reversible via "Rollback Last Restore".`
    )) return;
    setRestoreExecuteLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/case-restore-from-email/${encodeURIComponent(cid)}`,
        { confirm: true, proposed_employee_id: proposed.employee_id, restore_entity: restoreEntity },
        getAuthHeader()
      );
      toast.success(res.data?.message || 'Case restored');
      // Re-run forensics to show the new state + audit trail entry
      runForensics();
      runRestorePreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoreExecuteLoading(false);
    }
  };

  const runRestoreRollback = async () => {
    const cid = forensicsCaseId.trim();
    if (!cid) { toast.error('Enter a Case ID'); return; }
    if (!window.confirm(`Rollback the last restore for case ${cid}?`)) return;
    setRestoreExecuteLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/migrations/case-restore-rollback/${encodeURIComponent(cid)}`, {}, getAuthHeader());
      toast.success('Rolled back to previous linkage');
      runForensics();
      runRestorePreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rollback failed');
    } finally {
      setRestoreExecuteLoading(false);
    }
  };

  const runScanMixedUp = async () => {
    setScanLoading(true);
    setScanResult(null);
    try {
      const res = await axios.get(`${API}/api/admin/migrations/scan-mixed-up-cases?limit=500`, getAuthHeader());
      setScanResult(res.data);
      toast.success(`Scan complete — ${res.data?.mismatch_count || 0} mismatched case(s) found`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanLoading(false);
    }
  };

  // --- Revert Audit-Log Edits (field-level corruption repair) ---
  const [revertWindow, setRevertWindow] = useState({ from_time: '', to_time: '', by_user: '' });
  const [revertPreview, setRevertPreview] = useState(null);
  const [revertPreviewLoading, setRevertPreviewLoading] = useState(false);
  const [revertExecuteLoading, setRevertExecuteLoading] = useState(false);
  const [revertSelected, setRevertSelected] = useState({}); // audit_id → bool

  const runRevertPreview = async () => {
    const cid = forensicsCaseId.trim();
    if (!cid) { toast.error('Enter a Case ID'); return; }
    if (!revertWindow.from_time || !revertWindow.to_time) { toast.error('Set from/to time'); return; }
    setRevertPreviewLoading(true);
    setRevertPreview(null);
    try {
      const q = new URLSearchParams({
        from_time: revertWindow.from_time, to_time: revertWindow.to_time,
        ...(revertWindow.by_user ? { by_user: revertWindow.by_user } : {}),
      }).toString();
      const res = await axios.get(`${API}/api/admin/migrations/revert-audit-logs-preview/${encodeURIComponent(cid)}?${q}`, getAuthHeader());
      setRevertPreview(res.data);
      // Select all by default
      const sel = {};
      (res.data?.revertable || []).forEach(r => { sel[r.audit_id] = !r.already_reverted; });
      setRevertSelected(sel);
      toast.success(`Revert preview: ${res.data?.revertable_count || 0} revertable, ${res.data?.skipped_count || 0} skipped`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setRevertPreviewLoading(false);
    }
  };

  const runRevertExecute = async () => {
    const cid = forensicsCaseId.trim();
    const selIds = Object.entries(revertSelected).filter(([, v]) => v).map(([k]) => k);
    if (!selIds.length) { toast.error('Select at least one audit row'); return; }
    const applied = (revertPreview?.revertable || []).filter(r => selIds.includes(r.audit_id));
    const confirmMsg = `Revert ${applied.length} field(s) on employee ${revertPreview?.employee_id}?\n\n` +
      applied.slice(0, 10).map(r => `  ${r.field}: ${r.current_value} → ${r.old_value}`).join('\n') +
      (applied.length > 10 ? `\n  …and ${applied.length - 10} more` : '') +
      `\n\nPrevious values will be backed up under _field_revert_backup (reversible).`;
    if (!window.confirm(confirmMsg)) return;
    setRevertExecuteLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/revert-audit-logs/${encodeURIComponent(cid)}`,
        {
          confirm: true, audit_ids: selIds,
          from_time: revertWindow.from_time, to_time: revertWindow.to_time,
          by_user: revertWindow.by_user || null,
        },
        getAuthHeader()
      );
      toast.success(res.data?.message || 'Revert applied');
      runForensics();
      runRevertPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revert failed');
    } finally {
      setRevertExecuteLoading(false);
    }
  };

  const runRevertRollback = async () => {
    const cid = forensicsCaseId.trim();
    if (!cid) { toast.error('Enter a Case ID'); return; }
    if (!window.confirm(`Rollback the last field-revert on case ${cid}'s employee?`)) return;
    setRevertExecuteLoading(true);
    try {
      await axios.post(`${API}/api/admin/migrations/revert-audit-logs-rollback/${encodeURIComponent(cid)}`, {}, getAuthHeader());
      toast.success('Rolled back to pre-revert snapshot');
      runForensics();
      runRevertPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rollback failed');
    } finally {
      setRevertExecuteLoading(false);
    }
  };

  // --- N/A by Declaration scan + revert ---
  const [naScanLoading, setNaScanLoading] = useState(false);
  const [naScanResult, setNaScanResult] = useState(null);
  const [naSelected, setNaSelected] = useState({}); // {case_id_check_id: bool}
  const [naExecuteLoading, setNaExecuteLoading] = useState(false);

  const runNaScan = async () => {
    setNaScanLoading(true);
    setNaScanResult(null);
    try {
      const res = await axios.get(`${API}/api/admin/migrations/declaration-na-scan?limit=500&only_likely_bug=true`, getAuthHeader());
      setNaScanResult(res.data);
      const sel = {};
      (res.data?.findings || []).forEach(f => f.na_checks.forEach(n => { sel[`${f.case_id}__${n.check_id}`] = true; }));
      setNaSelected(sel);
      toast.success(`Found ${res.data?.case_count || 0} affected case(s) — ${res.data?.total_na_checks || 0} likely-bug N/A check(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setNaScanLoading(false);
    }
  };

  const runNaRevert = async (caseId) => {
    const finding = (naScanResult?.findings || []).find(f => f.case_id === caseId);
    if (!finding) return;
    const checkIds = finding.na_checks.map(n => n.check_id).filter(id => naSelected[`${caseId}__${id}`]);
    if (!checkIds.length) { toast.error('Select at least one check'); return; }
    if (!window.confirm(`Revert ${checkIds.length} N/A-by-Declaration check(s) on case ${caseId} to status='pending'?\n\nA backup will be saved on the case (reversible via Rollback).`)) return;
    setNaExecuteLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/migrations/revert-declaration-na`, {
        confirm: true, case_id: caseId, check_ids: checkIds, new_status: 'pending'
      }, getAuthHeader());
      toast.success(`Reverted ${res.data?.reverted_count} check(s) on ${caseId}`);
      runNaScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revert failed');
    } finally {
      setNaExecuteLoading(false);
    }
  };

  const runNaRollback = async (caseId) => {
    if (!window.confirm(`Rollback the last revert on case ${caseId}? Restores N/A-by-Declaration status from backup.`)) return;
    setNaExecuteLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/migrations/revert-declaration-na-rollback/${encodeURIComponent(caseId)}`, {}, getAuthHeader());
      toast.success(`Rolled back ${res.data?.rolled_back_count} check(s)`);
      runNaScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rollback failed');
    } finally {
      setNaExecuteLoading(false);
    }
  };

  // --- Login-failure forensics (diagnoses temp-password / "Invalid credentials" complaints) ---
  const [loginFailFilters, setLoginFailFilters] = useState({ email_contains: '', failure_reason: '', since_hours: '24' });
  const [loginFailLoading, setLoginFailLoading] = useState(false);
  const [loginFailResult, setLoginFailResult] = useState(null);

  const runLoginFailScan = async () => {
    setLoginFailLoading(true);
    setLoginFailResult(null);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (loginFailFilters.email_contains) params.append('email_contains', loginFailFilters.email_contains);
      if (loginFailFilters.failure_reason) params.append('failure_reason', loginFailFilters.failure_reason);
      if (loginFailFilters.since_hours) params.append('since_hours', loginFailFilters.since_hours);
      const res = await axios.get(`${API}/api/admin/migrations/login-failures-recent?${params.toString()}`, getAuthHeader());
      setLoginFailResult(res.data);
      toast.success(`${res.data?.count || 0} login failure(s) in window`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setLoginFailLoading(false);
    }
  };

  // ---- Stuck Drafts Scan (May 7 production bug forensics) ----
  const [stuckDays, setStuckDays] = useState('30');
  const [stuckLoading, setStuckLoading] = useState(false);
  const [stuckResult, setStuckResult] = useState(null);

  const runStuckDraftsScan = async () => {
    setStuckLoading(true);
    setStuckResult(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/migrations/stuck-drafts-scan?days=${encodeURIComponent(stuckDays || '30')}`,
        getAuthHeader()
      );
      setStuckResult(res.data);
      toast.success(`${res.data?.count || 0} stuck draft candidate(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setStuckLoading(false);
    }
  };

  // ---- Orphan Cases Scan + Heal (May 11 production bulk-import bug) ----
  const [orphanDays, setOrphanDays] = useState('30');
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [orphanResult, setOrphanResult] = useState(null);
  const [orphanSelected, setOrphanSelected] = useState(new Set());
  const [orphanHealing, setOrphanHealing] = useState(false);  const runOrphanScan = async () => {
    setOrphanLoading(true);
    setOrphanResult(null);
    setOrphanSelected(new Set());
    try {
      const res = await axios.get(
        `${API}/api/admin/migrations/orphan-cases-scan?days=${encodeURIComponent(orphanDays || '30')}`,
        getAuthHeader()
      );
      setOrphanResult(res.data);
      toast.success(`${res.data?.count || 0} orphan case(s) — ${res.data?.healable_count || 0} healable`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Orphan scan failed');
    } finally {
      setOrphanLoading(false);
    }
  };

  const toggleOrphanSelect = (caseId) => {
    setOrphanSelected(prev => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  };

  const selectAllHealable = () => {
    const healable = (orphanResult?.rows || []).filter(r => r.healable).map(r => r.case_id);
    setOrphanSelected(new Set(healable));
  };

  const runOrphanHeal = async (dryRun = false) => {
    const ids = Array.from(orphanSelected);
    if (ids.length === 0) {
      toast.info('Select at least one case to heal');
      return;
    }
    if (!dryRun && !window.confirm(`Heal ${ids.length} case(s)? This will backfill missing fields and write an audit trail entry. Reversible via revert tool.`)) {
      return;
    }
    setOrphanHealing(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/heal-orphan-cases`,
        { case_ids: ids, dry_run: dryRun },
        getAuthHeader()
      );
      const s = res.data?.summary || {};
      if (dryRun) {
        toast.info(`Dry run: would heal ${s.healed}, skip ${s.skipped}, fail ${s.failed}`);
      } else {
        toast.success(`Healed ${s.healed} case(s), skipped ${s.skipped}, failed ${s.failed}`);
        // Re-scan so the healed rows drop off the list
        await runOrphanScan();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Heal failed');
    } finally {
      setOrphanHealing(false);
    }
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // ---- Missing User Account Scan + Heal (May 11 production auth fallout) ----
  const [missingDays, setMissingDays] = useState('0');
  const [missingLoading, setMissingLoading] = useState(false);
  const [missingResult, setMissingResult] = useState(null);
  const [missingSelected, setMissingSelected] = useState(new Set());
  const [missingHealing, setMissingHealing] = useState(false);
  const [missingHealResult, setMissingHealResult] = useState(null);

  const runMissingUserScan = async () => {
    setMissingLoading(true);
    setMissingResult(null);
    setMissingSelected(new Set());
    setMissingHealResult(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/migrations/missing-user-accounts-scan?days=${encodeURIComponent(missingDays || '0')}`,
        getAuthHeader()
      );
      setMissingResult(res.data);
      toast.success(`${res.data?.count || 0} employee(s) without user accounts`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Missing-user scan failed');
    } finally {
      setMissingLoading(false);
    }
  };

  const toggleMissingSelect = (id) => {
    setMissingSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runMissingUserHeal = async (dryRun = false) => {
    const ids = Array.from(missingSelected);
    if (ids.length === 0) { toast.info('Select at least one row'); return; }
    if (!dryRun && !window.confirm(`Create user accounts for ${ids.length} candidate(s)? Each will get a fresh temp password.`)) return;
    setMissingHealing(true);
    setMissingHealResult(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/heal-missing-user-accounts`,
        { employee_ids: ids, dry_run: dryRun },
        getAuthHeader()
      );
      setMissingHealResult(res.data);
      const s = res.data?.summary || {};
      if (dryRun) toast.info(`Dry run: would heal ${s.healed}, skip ${s.skipped}, fail ${s.failed}`);
      else {
        toast.success(`Created ${s.healed} user account(s) · ${s.skipped} skipped · ${s.failed} failed`);
        await runMissingUserScan();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Heal failed');
    } finally {
      setMissingHealing(false);
    }
  };

  // ---- Empty Case Checks Recovery (May 14 — case stuck on Draft, "No verification checks configured") ----
  const [emptyChecksCaseId, setEmptyChecksCaseId] = useState('');
  const [emptyChecksOverride, setEmptyChecksOverride] = useState('');
  const [emptyChecksScanLoading, setEmptyChecksScanLoading] = useState(false);
  const [emptyChecksScan, setEmptyChecksScan] = useState(null);
  const [emptyChecksGlobalLoading, setEmptyChecksGlobalLoading] = useState(false);
  const [emptyChecksGlobal, setEmptyChecksGlobal] = useState(null);
  const [emptyChecksHealLoading, setEmptyChecksHealLoading] = useState(false);
  const [emptyChecksPreview, setEmptyChecksPreview] = useState(null);

  const runEmptyChecksScan = async () => {
    if (!emptyChecksCaseId.trim()) { toast.error('Enter a Case ID'); return; }
    setEmptyChecksScanLoading(true);
    setEmptyChecksScan(null);
    setEmptyChecksPreview(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/migrations/empty-case-checks-scan?case_id=${encodeURIComponent(emptyChecksCaseId.trim())}`,
        getAuthHeader()
      );
      setEmptyChecksScan(res.data);
      if (res.data?.broken) toast.warning('Case has empty checks — heal recommended');
      else toast.info(`Case looks healthy (${res.data?.case?.checks_count} checks)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setEmptyChecksScanLoading(false);
    }
  };

  const runEmptyChecksGlobalScan = async () => {
    setEmptyChecksGlobalLoading(true);
    setEmptyChecksGlobal(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/migrations/empty-case-checks-scan`,
        getAuthHeader()
      );
      setEmptyChecksGlobal(res.data);
      toast.success(`${res.data?.total_broken || 0} case(s) with empty checks`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Global scan failed');
    } finally {
      setEmptyChecksGlobalLoading(false);
    }
  };

  const runEmptyChecksHeal = async (dryRun) => {
    if (!emptyChecksCaseId.trim()) { toast.error('Enter a Case ID first'); return; }
    if (!dryRun && !window.confirm(`Apply heal to case ${emptyChecksCaseId.trim()}? This writes checks to the case.`)) return;
    setEmptyChecksHealLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/heal-empty-case-checks`,
        {
          case_id: emptyChecksCaseId.trim(),
          dry_run: dryRun,
          package_id_override: emptyChecksOverride.trim() || null,
        },
        getAuthHeader()
      );
      setEmptyChecksPreview(res.data);
      if (dryRun) toast.info(`Dry run: would add ${res.data?.proposed_checks_count} check(s)`);
      else toast.success(res.data?.message || 'Heal applied');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Heal failed');
    } finally {
      setEmptyChecksHealLoading(false);
    }
  };

  // ---- Inactive Agent Assignment Mismatch (May 15) ----
  const [inactiveAgentScan, setInactiveAgentScan] = useState(null);
  const [inactiveAgentScanLoading, setInactiveAgentScanLoading] = useState(false);
  const [inactiveAgentHealLoading, setInactiveAgentHealLoading] = useState(false);
  const [inactiveAgentHealResult, setInactiveAgentHealResult] = useState(null);

  const runInactiveAgentScan = async () => {
    setInactiveAgentScanLoading(true);
    setInactiveAgentScan(null);
    setInactiveAgentHealResult(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/migrations/inactive-agent-assignment-mismatch-scan`,
        getAuthHeader()
      );
      setInactiveAgentScan(res.data);
      toast.success(`${res.data?.total_mismatches || 0} mismatch(es) found`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setInactiveAgentScanLoading(false);
    }
  };

  const runInactiveAgentHeal = async (dryRun) => {
    if (!dryRun && !window.confirm('Apply heal? This aligns agent_assignments.is_active with users.is_active for every mismatched row.')) return;
    setInactiveAgentHealLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/heal-inactive-agent-assignments`,
        { dry_run: dryRun },
        getAuthHeader()
      );
      setInactiveAgentHealResult(res.data);
      if (dryRun) toast.info(`Dry run: would heal ${res.data?.total_would_heal} row(s)`);
      else toast.success(`Healed ${res.data?.total_healed} row(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Heal failed');
    } finally {
      setInactiveAgentHealLoading(false);
    }
  };

  // ---- Password Hash Drift Heal (May 18 — fixes recurring bcrypt_mismatch on invitation) ----
  const [pwDriftScan, setPwDriftScan] = useState(null);
  const [pwDriftScanLoading, setPwDriftScanLoading] = useState(false);
  const [pwDriftHealLoading, setPwDriftHealLoading] = useState(false);
  const [pwDriftHealResult, setPwDriftHealResult] = useState(null);

  const runPwDriftScan = async () => {
    setPwDriftScanLoading(true);
    setPwDriftScan(null);
    setPwDriftHealResult(null);
    try {
      const res = await axios.get(`${API}/api/admin/migrations/password-hash-drift-scan`, getAuthHeader());
      setPwDriftScan(res.data);
      toast.success(`${(res.data?.total_both_set || 0) + (res.data?.total_legacy_only || 0)} row(s) need migration`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setPwDriftScanLoading(false);
    }
  };

  const runPwDriftHeal = async (dryRun) => {
    if (!dryRun && !window.confirm('Apply heal? Consolidates password storage on the canonical `password_hash` field. No user passwords change — existing logins keep working.')) return;
    setPwDriftHealLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/heal-password-hash-drift`,
        { dry_run: dryRun },
        getAuthHeader()
      );
      setPwDriftHealResult(res.data);
      if (dryRun) toast.info(`Dry run: would migrate ${res.data?.total_would_heal} row(s)`);
      else toast.success(`Migrated ${res.data?.total_healed} row(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Heal failed');
    } finally {
      setPwDriftHealLoading(false);
    }
  };






  const runCaseDiagnosis = async () => {
    const ids = diagCaseIds.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    if (ids.length === 0) { toast.error('Enter at least one Case ID'); return; }
    setDiagLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/migrations/diagnose-cases`, { case_ids: ids }, getAuthHeader());
      setDiagResults(res.data.diagnosis);
      toast.success(`Diagnosis complete for ${res.data.diagnosis.length} cases`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Diagnosis failed');
    } finally { setDiagLoading(false); }
  };

  const runRepairCases = async (force = false) => {
    const ids = (diagResults || []).map(d => d.case_id).filter(Boolean);
    if (ids.length === 0) { toast.error('Run diagnosis first'); return; }
    const msg = force 
      ? `Force re-repair ${ids.length} case(s)? This will overwrite ALL sub_options using the package configuration.`
      : `Repair ${ids.length} case(s)? This will restore missing sub_option fields.`;
    if (!window.confirm(msg)) return;
    setRepairLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/migrations/repair-reset-cases`, { case_ids: ids, force }, getAuthHeader());
      const repaired = res.data.repaired || [];
      const totalFixes = repaired.reduce((sum, r) => sum + (r.repairs_count || 0), 0);
      toast.success(`Repaired ${repaired.length} case(s) with ${totalFixes} fixes`);
      runCaseDiagnosis();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Repair failed');
    } finally { setRepairLoading(false); }
  };

  const runBackfillCompletedAt = async () => {
    if (!window.confirm('Backfill completed_at on all completed cases missing this field?')) return;
    setLoading(prev => ({ ...prev, 'backfill-completed-at': true }));
    try {
      const res = await axios.post(`${API}/api/admin/migrations/backfill-completed-at`, {}, getAuthHeader());
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    } finally { setLoading(prev => ({ ...prev, 'backfill-completed-at': false })); }
  };

  const runResetBulkImportPasswords = async () => {
    if (!window.confirm("Reset password to 'Welcome@2026' for ALL employee users still pending first-login reset?\n\nThis fixes 'Invalid credentials' on first login for bulk-imported candidates. Each user is still required to change password on first login.")) return;
    setLoading(prev => ({ ...prev, 'reset-bulk-passwords': true }));
    try {
      const res = await axios.post(`${API}/api/admin/migrations/reset-bulk-import-passwords`, {}, getAuthHeader());
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setLoading(prev => ({ ...prev, 'reset-bulk-passwords': false })); }
  };

  const runPreview = async (migrationId) => {
    setLoading(prev => ({ ...prev, [migrationId + '-preview']: true }));
    try {
      const prefix = (migrationPrefixes[migrationId] || '').trim();
      let url = prefix
        ? `${API}/api/admin/migrations/${migrationId}/preview?case_prefix=${encodeURIComponent(prefix)}`
        : `${API}/api/admin/migrations/${migrationId}/preview`;
      // Pass the form-pending filter for the Basic Initiated tool
      if (migrationId === 'basic-initiated') {
        const sep = url.includes('?') ? '&' : '?';
        url = `${url}${sep}only_form_pending=${onlyFormPending ? 'true' : 'false'}`;
      }
      const res = await axios.get(url, getAuthHeader());
      setPreviews(prev => ({ ...prev, [migrationId]: res.data }));
      toast.success(`Preview loaded: ${res.data.count ?? res.data.total_orphaned ?? 0} records found`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(prev => ({ ...prev, [migrationId + '-preview']: false }));
    }
  };

  const runExecute = async (migrationId, selectedIds = []) => {
    const prefix = (migrationPrefixes[migrationId] || '').trim();
    const scopeLabel = prefix ? `case_prefix=${prefix}` : (selectedIds.length > 0 ? `${selectedIds.length} selected cases` : 'ALL eligible cases');
    if (!window.confirm(`Are you sure you want to execute the "${migrationId}" migration on ${scopeLabel}? This will modify production data.`)) return;
    setLoading(prev => ({ ...prev, [migrationId + '-execute']: true }));
    try {
      const body = {};
      if (selectedIds.length > 0) body.case_ids = selectedIds;
      if (prefix) body.case_prefix = prefix;
      const res = await axios.post(`${API}/api/admin/migrations/${migrationId}/execute`, body, getAuthHeader());
      setResults(prev => ({ ...prev, [migrationId]: res.data }));
      setPreviews(prev => ({ ...prev, [migrationId]: null }));
      setSelectedMigrationCases(new Set());
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setLoading(prev => ({ ...prev, [migrationId + '-execute']: false }));
    }
  };

  const runRollback = async (migrationId) => {
    if (!window.confirm(`Are you sure you want to ROLLBACK the "${migrationId}" migration?`)) return;
    setLoading(prev => ({ ...prev, [migrationId + '-rollback']: true }));
    try {
      const res = await axios.post(`${API}/api/admin/migrations/${migrationId}/rollback`, {}, getAuthHeader());
      setResults(prev => ({ ...prev, [migrationId]: res.data }));
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rollback failed');
    } finally {
      setLoading(prev => ({ ...prev, [migrationId + '-rollback']: false }));
    }
  };

  const runDiagnose = async () => {
    setLoading(prev => ({ ...prev, 'diagnose': true }));
    try {
      const url = `${API}/api/admin/migrations/basic-initiated/diagnose?only_form_pending=${onlyFormPending ? 'true' : 'false'}`;
      const res = await axios.get(url, getAuthHeader());
      setDiagnostic(res.data);
      toast.success(`Diagnosis complete: ${res.data.pending_qc_not_submitted_count} cases found`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Diagnosis failed');
    } finally {
      setLoading(prev => ({ ...prev, 'diagnose': false }));
    }
  };

  const runDeleteSelected = async () => {
    if (selectedOrphans.size === 0) {
      toast.error('No employees selected for deletion');
      return;
    }
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE ${selectedOrphans.size} selected employees? This cannot be undone.`)) return;
    setLoading(prev => ({ ...prev, 'clean-orphaned-employees-execute': true }));
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/clean-orphaned-employees/execute-selected`,
        { employee_ids: Array.from(selectedOrphans) },
        getAuthHeader()
      );
      setResults(prev => ({ ...prev, 'clean-orphaned-employees': res.data }));
      setPreviews(prev => ({ ...prev, 'clean-orphaned-employees': null }));
      setSelectedOrphans(new Set());
      setEntityFilter('all');
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Selective deletion failed');
    } finally {
      setLoading(prev => ({ ...prev, 'clean-orphaned-employees-execute': false }));
    }
  };

  return (
    <div data-testid="admin-migrations-page" className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <Database className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Database Migrations</h1>
        </div>

        {/* Case Diagnostic Tool */}
        <Card className="mb-6 border-blue-200" data-testid="case-diagnostic-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="h-5 w-5 text-blue-600" />
              Case Diagnostic Tool
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">Compare case structures to diagnose PDF report generation issues. Enter case IDs to inspect check fields, reset history, and missing data.</p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-4">
              <Input
                data-testid="diag-case-ids-input"
                placeholder="Enter Case IDs (comma-separated, e.g. ITC300326_00271, ITC270326_00253)"
                value={diagCaseIds}
                onChange={(e) => setDiagCaseIds(e.target.value)}
                className="flex-1"
              />
              <Button data-testid="run-diagnosis-btn" onClick={runCaseDiagnosis} disabled={diagLoading} className="bg-blue-600 hover:bg-blue-700">
                {diagLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Diagnose
              </Button>
            </div>

            {/* Backfill and Repair Tools */}
            <div className="flex items-center gap-2 flex-wrap p-3 bg-slate-50 rounded-lg border">
              <span className="text-sm font-medium text-slate-600 mr-2">Quick Actions:</span>
              <Button data-testid="backfill-completed-at-btn" onClick={runBackfillCompletedAt} disabled={loading['backfill-completed-at']} variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                {loading['backfill-completed-at'] ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Wrench className="h-3 w-3 mr-1" />}
                Backfill Completed Dates
              </Button>
              <Button data-testid="reset-bulk-passwords-btn" onClick={runResetBulkImportPasswords} disabled={loading['reset-bulk-passwords']} variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" title="Reset password to Welcome@2026 for all bulk-imported candidates pending first-login reset">
                {loading['reset-bulk-passwords'] ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Wrench className="h-3 w-3 mr-1" />}
                Reset Bulk-Import Passwords
              </Button>
              <Button data-testid="fix-insuff-status-btn" onClick={async () => {
                if (!window.confirm('Fix cases incorrectly set to insufficiency status? Cases with active checks will be restored to in_progress.')) return;
                setLoading(prev => ({ ...prev, 'fix-insuff': true }));
                try {
                  const res = await axios.post(`${API}/api/admin/migrations/fix-insufficiency-status`, {}, getAuthHeader());
                  toast.success(res.data.message);
                  if (res.data.skipped?.length > 0) {
                    const skippedIds = res.data.skipped.map(s => s.case_id).join(', ');
                    toast.info(`Correctly kept as insufficiency (no active checks): ${skippedIds}`, { duration: 10000 });
                  }
                } catch (err) { toast.error(err.response?.data?.detail || 'Fix failed'); }
                finally { setLoading(prev => ({ ...prev, 'fix-insuff': false })); }
              }} disabled={loading['fix-insuff']} variant="outline" size="sm" className="border-orange-300 text-orange-700 hover:bg-orange-50">
                {loading['fix-insuff'] ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Wrench className="h-3 w-3 mr-1" />}
                Fix Insufficiency Status
              </Button>
              <Button data-testid="fix-stale-insuff-btn" onClick={async () => {
                setLoading(prev => ({ ...prev, 'stale-insuff-preview': true }));
                try {
                  const res = await axios.get(`${API}/api/admin/migrations/stale-insufficiency-flags/preview`, getAuthHeader());
                  const data = res.data;
                  if (data.total_stale === 0) {
                    toast.success(`No stale flags found. ${data.total_open_insufficiencies} open insufficiencies are valid.`);
                  } else {
                    const doFix = window.confirm(`Found ${data.total_stale} checks with stale has_insufficiency flag (no open insufficiency in DB).\n\nSample:\n${data.stale_checks.slice(0,5).map(c => `${c.case_id} - ${c.check_type} (${c.assigned_to_name})`).join('\n')}\n\nClear stale flags?`);
                    if (doFix) {
                      const execRes = await axios.post(`${API}/api/admin/migrations/stale-insufficiency-flags/execute`, {}, getAuthHeader());
                      toast.success(execRes.data.message);
                    }
                  }
                } catch (err) { toast.error('Failed to check stale flags'); }
                finally { setLoading(prev => ({ ...prev, 'stale-insuff-preview': false })); }
              }} disabled={loading['stale-insuff-preview']} variant="outline" size="sm" className="border-purple-300 text-purple-700 hover:bg-purple-50">
                {loading['stale-insuff-preview'] ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Wrench className="h-3 w-3 mr-1" />}
                Fix Stale Insufficiency Flags
              </Button>
              <Button data-testid="fix-orphaned-vendor-btn" onClick={async () => {
                setLoading(prev => ({ ...prev, 'orphaned-vendor': true }));
                try {
                  const res = await axios.get(`${API}/api/admin/migrations/orphaned-vendor-checks/preview`, getAuthHeader());
                  const data = res.data;
                  if (data.total_orphaned === 0) {
                    toast.success('No orphaned vendor checks found.');
                  } else {
                    const details = data.orphaned_checks.slice(0, 10).map(c => 
                      `${c.case_id} - ${c.check_type} ${c.sub_option || ''} (${c.assigned_to_name || 'no agent'}) [${c.issues.join(', ')}]`
                    ).join('\n');
                    const doFix = window.confirm(`Found ${data.total_orphaned} orphaned vendor-assigned checks.\n\nSample:\n${details}\n\nFix by setting proper vendor_id from assigned user's vendor profile?`);
                    if (doFix) {
                      const execRes = await axios.post(`${API}/api/admin/migrations/orphaned-vendor-checks/execute`, {}, getAuthHeader());
                      toast.success(execRes.data.message);
                    }
                  }
                } catch (err) { toast.error('Failed to check orphaned vendor checks'); }
                finally { setLoading(prev => ({ ...prev, 'orphaned-vendor': false })); }
              }} disabled={loading['orphaned-vendor']} variant="outline" size="sm" className="border-teal-300 text-teal-700 hover:bg-teal-50">
                {loading['orphaned-vendor'] ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Wrench className="h-3 w-3 mr-1" />}
                Fix Orphaned Vendor Checks
              </Button>
            </div>

            {diagResults && (
              <div className="space-y-4">
                {diagResults.some(d => d.was_reset || d.checks?.some(c => !c.sub_option || c.fields_missing?.includes('sub_option'))) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button data-testid="repair-cases-btn" onClick={() => runRepairCases(false)} disabled={repairLoading} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50">
                      {repairLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wrench className="h-4 w-4 mr-1" />}
                      Repair Missing sub_options
                    </Button>
                    <Button data-testid="force-repair-btn" onClick={() => runRepairCases(true)} disabled={repairLoading} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">
                      {repairLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wrench className="h-4 w-4 mr-1" />}
                      Force Re-repair ALL sub_options (from package)
                    </Button>
                  </div>
                )}

                {diagResults.map((diag) => (
                  <Card key={diag.case_id} className={`border ${diag.was_reset ? 'border-orange-300 bg-orange-50/30' : 'border-green-300 bg-green-50/30'}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className="font-mono font-bold text-sm">{diag.case_id}</span>
                        <Badge variant={diag.status === 'completed' ? 'default' : 'secondary'} className={diag.status === 'completed' ? 'bg-green-600' : ''}>{diag.status}</Badge>
                        {diag.overall_result && <Badge variant="outline">{diag.overall_result}</Badge>}
                        {diag.was_reset && <Badge className="bg-orange-500">RESET x{diag.reset_count}</Badge>}
                        {diag.error && <Badge variant="destructive">{diag.error}</Badge>}
                      </div>

                      {diag.reset_history?.length > 0 && (
                        <div className="text-xs text-orange-700 mb-2">
                          Reset history: {diag.reset_history.map((r, i) => <span key={i}>{r.timestamp} by {r.user}{i < diag.reset_history.length - 1 ? ', ' : ''}</span>)}
                        </div>
                      )}

                      {diag.checks && (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-xs">#</TableHead>
                                <TableHead className="text-xs">Check Type</TableHead>
                                <TableHead className="text-xs">Status</TableHead>
                                <TableHead className="text-xs">sub_option</TableHead>
                                <TableHead className="text-xs">verification_result</TableHead>
                                <TableHead className="text-xs">has_verified_data</TableHead>
                                <TableHead className="text-xs">Missing Fields</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {diag.checks.map((ch) => (
                                <TableRow key={ch.index} className={!ch.sub_option ? 'bg-red-50' : ''}>
                                  <TableCell className="text-xs font-mono">{ch.check_index || ch.index}</TableCell>
                                  <TableCell className="text-xs">{typeof ch.check_type === 'object' ? ch.check_type?.check_type : ch.check_type}</TableCell>
                                  <TableCell><Badge variant="outline" className="text-xs">{ch.status}</Badge></TableCell>
                                  <TableCell className={`text-xs font-mono ${ch.sub_option ? 'text-green-700' : 'text-red-600 font-bold'}`}>{ch.sub_option || 'MISSING'}</TableCell>
                                  <TableCell className="text-xs">{ch.verification_result || '-'}</TableCell>
                                  <TableCell className="text-xs">{ch.has_verified_data ? 'Yes' : 'No'}</TableCell>
                                  <TableCell className="text-xs text-red-600 max-w-xs truncate">{ch.fields_missing?.filter(f => !['drug_test','reference_verification','criminal_verification'].includes(f)).join(', ') || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Case Forensics & Recovery */}
        <Card className="mb-6 border-rose-200" data-testid="case-forensics-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-600" />
              Case Forensics & Recovery
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Investigate cases where data appears swapped/overwritten. Builds a unified timeline from <strong>case_audit_trail</strong>, <strong>verification_email_logs</strong>, <strong>audit_logs</strong>, <strong>insufficiencies</strong>, <strong>vendor_assignments</strong>, <strong>candidate_drafts</strong> and the case doc itself, and flags candidate-name mismatches between sent emails and the current case linkage. Includes one-click <strong>Restore from email-log snapshot</strong>.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3">
              <Input
                data-testid="forensics-case-id-input"
                placeholder="Enter Case ID (e.g. RTX290426_00973)"
                value={forensicsCaseId}
                onChange={(e) => setForensicsCaseId(e.target.value)}
                className="flex-1"
              />
              <Button data-testid="run-forensics-btn" onClick={runForensics} disabled={forensicsLoading} className="bg-rose-600 hover:bg-rose-700">
                {forensicsLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Build Timeline
              </Button>
              <Button data-testid="restore-preview-btn" onClick={runRestorePreview} disabled={restorePreviewLoading} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50">
                {restorePreviewLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
                Restore Preview
              </Button>
            </div>
            <div className="flex gap-2 mb-4 items-center flex-wrap">
              <Button data-testid="scan-mixed-up-btn" onClick={runScanMixedUp} disabled={scanLoading} variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                {scanLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Search className="h-3 w-3 mr-1" />}
                Scan All for Mix-ups
              </Button>
              <span className="text-xs text-slate-500">Bulk-scan every case with email logs and report mismatches against current employee_name.</span>
            </div>

            {/* Scan results */}
            {scanResult && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
                <p className="text-sm font-semibold text-amber-900 mb-2">
                  Scan: {scanResult.mismatch_count} mismatch(es) found
                </p>
                {(scanResult.findings || []).length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Case ID</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Email Candidate</TableHead>
                        <TableHead>Current Employee</TableHead>
                        <TableHead>Earliest Email</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scanResult.findings.map((f) => (
                        <TableRow key={f.case_id}>
                          <TableCell className="font-mono text-xs">{f.case_id}</TableCell>
                          <TableCell className="text-xs">{f.client_name}</TableCell>
                          <TableCell className="text-xs text-emerald-700">{f.email_candidate_name}</TableCell>
                          <TableCell className="text-xs text-rose-700">{f.current_employee_name}</TableCell>
                          <TableCell className="text-xs">{f.earliest_email_at?.substring(0, 10)}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => { setForensicsCaseId(f.case_id); setScanResult(null); setTimeout(() => runForensics(), 200); }}>
                              Open
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}

            {/* Restore Preview Block */}
            {restorePreview && (
              <Card className="mb-4 border-rose-300 bg-rose-50/50" data-testid="restore-preview-card">
                <CardContent className="pt-4">
                  <p className="text-sm font-semibold text-rose-900 mb-2 flex items-center gap-2">
                    <Wrench className="h-4 w-4" /> Restore from Email-Log Snapshot
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-white rounded p-3 border">
                      <p className="text-xs text-slate-500 mb-1">CURRENT (possibly wrong)</p>
                      <p className="font-medium text-rose-700">{restorePreview.current_employee?.employee_name || '-'}</p>
                      <p className="text-xs font-mono">{restorePreview.current_employee?.employee_id || '-'}</p>
                      <p className="text-xs text-slate-600 mt-1">Entity: {restorePreview.current_employee?.entity_name || '-'}</p>
                    </div>
                    <div className="bg-white rounded p-3 border">
                      <p className="text-xs text-slate-500 mb-1">PROPOSED (from earliest email)</p>
                      <p className="font-medium text-emerald-700">{restorePreview.proposed_employee?.employee_name || 'No match found'}</p>
                      <p className="text-xs font-mono">{restorePreview.proposed_employee?.employee_id || '-'}</p>
                      <p className="text-xs text-slate-600 mt-1">Entity: {restorePreview.proposed_employee?.entity_name || '-'}</p>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-slate-600">
                    <p>Original candidate (extracted from subject): <span className="font-semibold">{restorePreview.original_candidate_name_from_email}</span></p>
                    <p>Earliest email at: {restorePreview.earliest_email_at} · Subject: <span className="italic">{restorePreview.earliest_email_subject?.substring(0, 100)}</span></p>
                    <p>Match candidates found: {restorePreview.match_candidate_count}</p>
                  </div>
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={restoreEntity} onCheckedChange={(v) => setRestoreEntity(!!v)} data-testid="restore-entity-checkbox" />
                      Also restore entity_id
                    </label>
                    <Button data-testid="restore-execute-btn" disabled={!restorePreview.would_change || restoreExecuteLoading} onClick={runRestoreExecute} className="bg-rose-600 hover:bg-rose-700">
                      {restoreExecuteLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                      Execute Restore
                    </Button>
                    <Button data-testid="restore-rollback-btn" disabled={restoreExecuteLoading} onClick={runRestoreRollback} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                      {restoreExecuteLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
                      Rollback Last Restore
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Revert Audit-Log Edits (field-level corruption repair) */}
            <Card className="mb-4 border-rose-400 bg-rose-50/30" data-testid="revert-audit-logs-card">
              <CardContent className="pt-4">
                <p className="text-sm font-semibold text-rose-900 mb-2 flex items-center gap-2">
                  <Wrench className="h-4 w-4" /> Revert Audit-Log Edits (Field-Level)
                </p>
                <p className="text-xs text-slate-600 mb-3">
                  For the corruption pattern where <strong>employee_id stays the same</strong> but data fields got overwritten (e.g. name/father_name/dob/email/employment swapped).
                  Each row below comes from <code>audit_logs</code> and proposes writing <code>old_value</code> back to the employee doc.
                  <strong className="text-purple-700"> Nested fields</strong> (employment_entries, education_entries, address objects, references) are now <em>also</em> revertable — their <code>old_value</code> is parsed via <code>ast.literal_eval</code> and written back as a list/dict. Look for the <span className="border border-purple-300 text-purple-700 rounded px-1 text-[9px]">nested</span> badge.
                </p>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div>
                    <Label className="text-xs">From (UTC ISO)</Label>
                    <Input data-testid="revert-from-input" placeholder="2026-04-30T08:52:00+00:00" value={revertWindow.from_time} onChange={(e) => setRevertWindow({ ...revertWindow, from_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">To (UTC ISO)</Label>
                    <Input data-testid="revert-to-input" placeholder="2026-04-30T08:53:30+00:00" value={revertWindow.to_time} onChange={(e) => setRevertWindow({ ...revertWindow, to_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">By user (optional — name or role)</Label>
                    <Input data-testid="revert-by-user-input" placeholder="Sindhu / qc_operations" value={revertWindow.by_user} onChange={(e) => setRevertWindow({ ...revertWindow, by_user: e.target.value })} />
                  </div>
                </div>
                <div className="flex gap-2 mb-3 flex-wrap">
                  <Button data-testid="revert-preview-btn" onClick={runRevertPreview} disabled={revertPreviewLoading} className="bg-rose-600 hover:bg-rose-700">
                    {revertPreviewLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
                    Preview Revert
                  </Button>
                  <Button data-testid="revert-execute-btn" onClick={runRevertExecute} disabled={!revertPreview?.revertable?.length || revertExecuteLoading} className="bg-rose-700 hover:bg-rose-800">
                    {revertExecuteLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                    Execute Revert
                  </Button>
                  <Button data-testid="revert-rollback-btn" onClick={runRevertRollback} disabled={revertExecuteLoading} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                    {revertExecuteLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
                    Rollback Last Revert
                  </Button>
                </div>

                {/* Cross-check advisory */}
                {revertPreview?.cross_check && (
                  <div className="p-3 mb-3 bg-amber-50 border border-amber-300 rounded text-xs">
                    <p className="font-semibold text-amber-900 mb-1">Cross-check: other employees holding the overwriting name</p>
                    <p className="text-amber-800">{revertPreview.cross_check.advisory}</p>
                    {(revertPreview.cross_check.employees_with_that_name || []).map(o => (
                      <div key={o.id} className="mt-1">
                        • <span className="font-mono">{o.id}</span> · {o.name} · cases: {(o.cases || []).map(c => c.id).join(', ') || '(none)'}
                      </div>
                    ))}
                  </div>
                )}

                {/* Revert rows */}
                {revertPreview && (
                  <div className="border rounded">
                    <div className="px-3 py-2 bg-slate-100 text-xs font-semibold flex items-center justify-between">
                      <span>Revertable ({revertPreview.revertable_count}) · Skipped ({revertPreview.skipped_count})</span>
                      <span className="text-slate-500">Employee: <span className="font-mono">{revertPreview.employee_id}</span> · current name: <strong>{revertPreview.current_employee_name}</strong></span>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8"></TableHead>
                            <TableHead className="w-28">Field</TableHead>
                            <TableHead>OLD (will restore)</TableHead>
                            <TableHead>NEW / CURRENT</TableHead>
                            <TableHead className="w-28">By</TableHead>
                            <TableHead className="w-36">When (UTC)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(revertPreview.revertable || []).map((r) => (
                            <TableRow key={r.audit_id} className={r.already_reverted ? 'opacity-50' : ''}>
                              <TableCell>
                                <Checkbox
                                  checked={!!revertSelected[r.audit_id]}
                                  onCheckedChange={(v) => setRevertSelected(prev => ({ ...prev, [r.audit_id]: !!v }))}
                                  disabled={r.already_reverted}
                                  data-testid={`revert-row-check-${r.audit_id}`}
                                />
                              </TableCell>
                              <TableCell className="text-xs font-medium">
                                {r.field}
                                {r.is_nested && (
                                  <Badge variant="outline" className="ml-1 text-[9px] py-0 px-1 border-purple-300 text-purple-700">
                                    nested {r.old_value_parsed_type ? `· ${r.old_value_parsed_type}` : ''}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-emerald-700 max-w-md truncate" title={String(r.old_value || '')}>{r.old_value ?? <em>null</em>}</TableCell>
                              <TableCell className="text-xs text-rose-700 max-w-md truncate" title={String(r.current_value || '')}>{r.current_value ?? <em>null</em>}</TableCell>
                              <TableCell className="text-xs">{r.changed_by_name} <span className="text-slate-400">({r.changed_by_role})</span></TableCell>
                              <TableCell className="text-xs font-mono">{r.created_at?.substring(0, 19)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {(revertPreview.skipped || []).length > 0 && (
                        <div className="px-3 py-2 bg-slate-50 border-t text-xs text-slate-600">
                          <span className="font-semibold">Skipped (excluded or unparseable):</span>{' '}
                          {revertPreview.skipped.map((s, i) => (
                            <span key={i} className="inline-block mr-2">
                              <code>{s.field}</code>
                              <span className="text-slate-400"> ({s.reason})</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Forensics Result */}
            {forensicsResult && (
              <div className="space-y-3">
                {/* Anomalies */}
                {(forensicsResult.anomalies || []).length > 0 && (
                  <div className="p-3 bg-rose-50 border-2 border-rose-300 rounded">
                    <p className="text-sm font-semibold text-rose-900 mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" /> Anomalies Detected
                    </p>
                    {forensicsResult.anomalies.map((a, idx) => (
                      <div key={idx} className="text-xs text-rose-800 mb-1">
                        <span className="font-semibold uppercase">[{a.severity}] {a.type}</span> — {a.detail}
                      </div>
                    ))}
                  </div>
                )}

                {/* Current State */}
                <div className="p-3 bg-slate-50 rounded border">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Current State</p>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><span className="text-slate-500">Employee:</span> <span className="font-medium">{forensicsResult.current_state?.employee_name}</span></div>
                    <div><span className="text-slate-500">Entity:</span> <span className="font-medium">{forensicsResult.current_state?.entity_name}</span></div>
                    <div><span className="text-slate-500">Status:</span> <span className="font-medium">{forensicsResult.current_state?.status}</span></div>
                    <div><span className="text-slate-500">Employee ID:</span> <span className="font-mono">{forensicsResult.current_state?.employee_id}</span></div>
                    <div><span className="text-slate-500">Submitted:</span> <span>{forensicsResult.current_state?.submitted_at?.substring(0, 19) || '-'}</span></div>
                    <div><span className="text-slate-500">Completed:</span> <span>{forensicsResult.current_state?.completed_at?.substring(0, 19) || '-'}</span></div>
                  </div>
                </div>

                {/* Source counts */}
                <div className="p-3 bg-slate-50 rounded border text-xs">
                  <p className="font-semibold text-slate-700 mb-1">Source Counts</p>
                  <div className="flex gap-4 flex-wrap">
                    {Object.entries(forensicsResult.raw || {}).map(([k, v]) => (
                      <span key={k}><span className="text-slate-500">{k.replace(/_/g, ' ')}:</span> <span className="font-mono">{v}</span></span>
                    ))}
                  </div>
                </div>

                {/* Timeline */}
                <div className="border rounded">
                  <div className="px-3 py-2 bg-slate-100 text-sm font-semibold border-b flex items-center justify-between">
                    <span>Timeline ({forensicsResult.timeline_count || 0} events) — earliest first</span>
                    <Button size="sm" variant="ghost" onClick={() => {
                      const data = JSON.stringify(forensicsResult, null, 2);
                      const blob = new Blob([data], { type: 'application/json' });
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `forensics_${forensicsResult.case_id}.json`;
                      a.click();
                    }}>Export JSON</Button>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-44">When (UTC)</TableHead>
                          <TableHead className="w-36">Source</TableHead>
                          <TableHead>Action</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="w-32">Who</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(forensicsResult.timeline || []).map((ev, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-xs font-mono">{ev.timestamp?.substring(0, 19)}</TableCell>
                            <TableCell className="text-xs">
                              <Badge variant="outline" className="text-[10px]">{ev.source}</Badge>
                            </TableCell>
                            <TableCell className="text-xs font-medium">{ev.action}</TableCell>
                            <TableCell className="text-xs text-slate-600">{ev.description}</TableCell>
                            <TableCell className="text-xs">{ev.who} {ev.role ? <span className="text-slate-400">({ev.role})</span> : null}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* N/A by Declaration Forensics & Revert */}
        <Card className="mb-6 border-orange-200" data-testid="na-declaration-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              N/A by Declaration — Bug Scan & Revert
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Scans every case for checks stamped <code>not_applicable_by_declaration</code> and flags them as <strong>likely-bug</strong> when the candidate actually filled <em>≥</em> the required count of records (i.e., they did NOT opt for fewer checks but the system marked the check N/A anyway). Revert flips the status to <code>pending</code> so the agent's normal flow can re-process. Backup is saved per-case under <code>_declaration_na_backup</code> for rollback.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-4">
              <Button data-testid="run-na-scan-btn" onClick={runNaScan} disabled={naScanLoading} className="bg-orange-600 hover:bg-orange-700">
                {naScanLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Scan All Cases
              </Button>
            </div>

            {naScanResult && (
              <div className="space-y-3">
                <div className="p-3 bg-orange-50 border border-orange-200 rounded text-sm">
                  <span className="font-semibold text-orange-900">Scan complete:</span>{' '}
                  {naScanResult.case_count} case(s) affected · {naScanResult.total_na_checks} likely-bug N/A check(s).
                </div>
                {(naScanResult.findings || []).length === 0 ? (
                  <p className="text-sm text-slate-500">No likely-bug N/A-by-Declaration checks found.</p>
                ) : (
                  <div className="space-y-3">
                    {naScanResult.findings.map((f) => (
                      <Card key={f.case_id} className="border-orange-200" data-testid={`na-finding-${f.case_id}`}>
                        <CardContent className="pt-3 pb-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="text-sm">
                              <span className="font-mono font-semibold">{f.case_id}</span>{' '}
                              · <span className="font-medium">{f.employee_name}</span>{' '}
                              · <span className="text-slate-500">{f.client_name}</span>
                              <div className="text-xs text-slate-600 mt-1">
                                Filled — addresses: <strong>{f.addresses_filled}</strong> | employments: <strong>{f.employments_filled}</strong> | educations: <strong>{f.educations_filled}</strong> · Declaration flags — addr: <strong>{String(f.address_declaration)}</strong>, emp: <strong>{String(f.employment_declaration)}</strong>, edu: <strong>{String(f.education_declaration)}</strong>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" onClick={() => runNaRevert(f.case_id)} disabled={naExecuteLoading} className="bg-orange-600 hover:bg-orange-700" data-testid={`na-revert-${f.case_id}`}>
                                {naExecuteLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Play className="h-3 w-3 mr-1" />}
                                Revert Selected
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => runNaRollback(f.case_id)} disabled={naExecuteLoading} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`na-rollback-${f.case_id}`}>
                                <RotateCcw className="h-3 w-3 mr-1" /> Rollback
                              </Button>
                            </div>
                          </div>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-8"></TableHead>
                                <TableHead className="w-32">Check ID</TableHead>
                                <TableHead className="w-40">Type</TableHead>
                                <TableHead className="w-24">Required</TableHead>
                                <TableHead className="w-24">Filled</TableHead>
                                <TableHead className="w-24">Decl Flag</TableHead>
                                <TableHead>Declared by/at</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {f.na_checks.map((n) => {
                                const key = `${f.case_id}__${n.check_id}`;
                                return (
                                  <TableRow key={key}>
                                    <TableCell>
                                      <Checkbox
                                        checked={!!naSelected[key]}
                                        onCheckedChange={(v) => setNaSelected(prev => ({ ...prev, [key]: !!v }))}
                                        data-testid={`na-row-check-${key}`}
                                      />
                                    </TableCell>
                                    <TableCell className="text-xs font-mono">{n.check_id}</TableCell>
                                    <TableCell className="text-xs">{n.check_type}</TableCell>
                                    <TableCell className="text-xs text-center">{n.required_count}</TableCell>
                                    <TableCell className={`text-xs text-center ${n.filled_count >= n.required_count ? 'text-emerald-700 font-semibold' : 'text-rose-700'}`}>{n.filled_count}</TableCell>
                                    <TableCell className="text-xs">{String(n.declaration_flag)}</TableCell>
                                    <TableCell className="text-xs text-slate-600">{n.declared_by} · {n.declared_at?.substring(0, 19)}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Login-Failure Forensics */}
        <Card className="mb-6 border-blue-200" data-testid="login-failures-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-blue-600" />
              Login Failure Forensics
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Diagnose recurring "Invalid credentials" complaints. Distinguishes <code>user_not_found</code> (typo email), <code>bcrypt_mismatch</code> (wrong password OR storage bug), and flags whitespace-padded passwords + temp-password attempts. Reads from <code>activity_logs</code> (action_type = login_failed / login_blocked).
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <Label className="text-xs">Email contains</Label>
                <Input data-testid="loginfail-email-input" placeholder="e.g. candidate@example.com" value={loginFailFilters.email_contains} onChange={(e) => setLoginFailFilters({ ...loginFailFilters, email_contains: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Failure reason (optional)</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm h-9" value={loginFailFilters.failure_reason} onChange={(e) => setLoginFailFilters({ ...loginFailFilters, failure_reason: e.target.value })} data-testid="loginfail-reason-select">
                  <option value="">(any reason)</option>
                  <option value="user_not_found">user_not_found</option>
                  <option value="bcrypt_mismatch">bcrypt_mismatch</option>
                  <option value="no_password_stored_on_user">no_password_stored_on_user</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Since (hours)</Label>
                <Input data-testid="loginfail-hours-input" type="number" placeholder="24" value={loginFailFilters.since_hours} onChange={(e) => setLoginFailFilters({ ...loginFailFilters, since_hours: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-2 mb-4">
              <Button data-testid="loginfail-scan-btn" onClick={runLoginFailScan} disabled={loginFailLoading} className="bg-blue-600 hover:bg-blue-700">
                {loginFailLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Fetch Failures
              </Button>
            </div>

            {loginFailResult && (
              <div className="space-y-3">
                {/* Summary panel */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-blue-50 rounded border text-sm">
                    <p className="font-semibold text-blue-900 mb-1">By reason ({loginFailResult.count} total)</p>
                    {Object.keys(loginFailResult.summary?.by_reason || {}).length === 0 ? (
                      <p className="text-xs text-slate-500">No failures in window</p>
                    ) : (
                      Object.entries(loginFailResult.summary.by_reason).map(([k, v]) => (
                        <div key={k} className="text-xs">
                          <code className={k === 'user_not_found' ? 'text-amber-700' : k === 'bcrypt_mismatch' ? 'text-rose-700' : 'text-slate-700'}>{k}</code>: <strong>{v}</strong>
                        </div>
                      ))
                    )}
                    <p className="text-xs mt-2 text-slate-600">
                      Temp-password attempts: <strong>{loginFailResult.summary?.temp_password_attempts || 0}</strong> · Whitespace-in-password: <strong className={loginFailResult.summary?.whitespace_in_password_attempts > 0 ? 'text-amber-700' : ''}>{loginFailResult.summary?.whitespace_in_password_attempts || 0}</strong>
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded border text-sm">
                    <p className="font-semibold text-blue-900 mb-1">Top failing emails</p>
                    {(loginFailResult.summary?.top_emails || []).length === 0 ? (
                      <p className="text-xs text-slate-500">—</p>
                    ) : (
                      loginFailResult.summary.top_emails.map((e) => (
                        <div key={e.email} className="text-xs">
                          {e.email}: <strong className={e.failures >= 5 ? 'text-rose-700' : ''}>{e.failures}</strong>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                {/* Rows */}
                <div className="border rounded">
                  <div className="px-3 py-2 bg-slate-100 text-xs font-semibold border-b">
                    Recent attempts (newest first)
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-36">When (UTC)</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead className="w-32">Reason</TableHead>
                          <TableHead className="w-24">Hash len</TableHead>
                          <TableHead className="w-24">Temp pwd?</TableHead>
                          <TableHead className="w-24">WS</TableHead>
                          <TableHead>Role</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(loginFailResult.rows || []).map((r) => {
                          const d = r.details || {};
                          const ws = d.submitted_has_leading_space || d.submitted_has_trailing_space;
                          return (
                            <TableRow key={r.id}>
                              <TableCell className="text-xs font-mono">{r.timestamp?.substring(0, 19)}</TableCell>
                              <TableCell className="text-xs">{r.user_email}</TableCell>
                              <TableCell className="text-xs">
                                <Badge variant="outline" className={`text-[10px] ${d.failure_reason === 'bcrypt_mismatch' ? 'border-rose-300 text-rose-700' : d.failure_reason === 'user_not_found' ? 'border-amber-300 text-amber-700' : 'border-slate-300 text-slate-700'}`}>
                                  {d.failure_reason || 'unknown'}
                                </Badge>
                              </TableCell>
                              <TableCell className={`text-xs font-mono ${d.stored_hash_length && d.stored_hash_length !== 60 ? 'text-rose-700 font-bold' : ''}`}>{d.stored_hash_length ?? '—'}</TableCell>
                              <TableCell className="text-xs">{d.is_temp_password_match ? <span className="text-amber-700 font-semibold">yes</span> : '—'}</TableCell>
                              <TableCell className="text-xs">{ws ? <span className="text-amber-700 font-semibold">yes</span> : '—'}</TableCell>
                              <TableCell className="text-xs">{d.user_role || '—'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                <div className="text-[11px] text-slate-500">
                  <strong>How to read this:</strong> Hash length should be <strong>60</strong> for healthy bcrypt. Anything other than 60 (e.g. 120 = double-hashed) is a storage bug.
                  <code> bcrypt_mismatch</code> + <code>is_temp_password_match=yes</code> on a fresh candidate strongly suggests the temp password write path double-hashed.
                  <code> WS=yes</code> means the candidate pasted a password with leading/trailing whitespace — they should retype, or we should auto-trim on the login page.
                </div>
              </div>
            )}
          </CardContent>
        </Card>


        {/* Stuck Drafts Scan — May 7 production incident forensics */}
        <Card className="mb-6 border-rose-200" data-testid="stuck-drafts-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-600" />
              Stuck Drafts Scan (Candidates Who Can't Save)
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Surfaces candidates whose documents uploaded successfully but whose form data (employment / education entries) is missing OR exceeds the package allowance. Read-only. Useful for confirming the scope of the <strong>May 7 package-quantity-guard incident</strong>.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <Label className="text-xs">Lookback (days)</Label>
                <Input
                  data-testid="stuck-days-input"
                  type="number"
                  min="1"
                  max="90"
                  value={stuckDays}
                  onChange={(e) => setStuckDays(e.target.value)}
                  placeholder="30"
                />
              </div>
            </div>
            <Button
              data-testid="stuck-scan-btn"
              onClick={runStuckDraftsScan}
              disabled={stuckLoading}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {stuckLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
              Scan Stuck Drafts
            </Button>

            {stuckResult && (
              <div className="mt-4 space-y-3">
                <div className="p-3 bg-rose-50 rounded border text-sm">
                  <p className="text-rose-900">
                    <strong>{stuckResult.count}</strong> stuck draft candidate(s) found in the last <strong>{stuckResult.scanned_days}</strong> days.
                  </p>
                  <p className="text-xs text-slate-600 mt-1">{stuckResult.explanation}</p>
                </div>

                {stuckResult.count > 0 && (
                  <div className="border rounded max-h-[480px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 sticky top-0">
                          <TableHead className="text-xs">Severity</TableHead>
                          <TableHead className="text-xs">Case ID</TableHead>
                          <TableHead className="text-xs">Candidate</TableHead>
                          <TableHead className="text-xs">Client / Entity</TableHead>
                          <TableHead className="text-xs">Package</TableHead>
                          <TableHead className="text-xs">Docs</TableHead>
                          <TableHead className="text-xs">Emp (filled/req)</TableHead>
                          <TableHead className="text-xs">Edu (filled/req)</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                          <TableHead className="text-xs">Flags</TableHead>
                          <TableHead className="text-xs">Last Activity</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stuckResult.rows.map((r) => (
                          <TableRow key={r.employee_id} data-testid={`stuck-draft-row-${r.case_id}`}>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={`text-[10px] ${
                                  r.severity === 'high'
                                    ? 'border-rose-300 text-rose-700 bg-rose-50'
                                    : 'border-amber-300 text-amber-700 bg-amber-50'
                                }`}
                              >
                                {r.severity}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs font-mono">{r.case_id}</TableCell>
                            <TableCell className="text-xs">
                              <div className="font-medium">{r.candidate_name}</div>
                              <div className="text-slate-500 text-[10px]">{r.candidate_email}</div>
                            </TableCell>
                            <TableCell className="text-xs">
                              <div>{r.client_name}</div>
                              <div className="text-slate-500 text-[10px]">{r.entity_name}</div>
                            </TableCell>
                            <TableCell className="text-xs">{r.package_name || '—'}</TableCell>
                            <TableCell className="text-xs text-center font-mono">{r.doc_count}</TableCell>
                            <TableCell className="text-xs font-mono">
                              <span className={r.employment_filled !== r.employment_required ? 'text-rose-700 font-bold' : ''}>
                                {r.employment_filled}/{r.employment_required}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs font-mono">
                              <span className={r.education_filled !== r.education_required ? 'text-rose-700 font-bold' : ''}>
                                {r.education_filled}/{r.education_required}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs">{r.case_status}</TableCell>
                            <TableCell className="text-xs">
                              <ul className="text-[10px] list-disc pl-3">
                                {(r.flags || []).map((f, i) => (<li key={i}>{f}</li>))}
                              </ul>
                            </TableCell>
                            <TableCell className="text-[10px] font-mono">{(r.case_updated_at || '').substring(0, 19)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <div className="text-[11px] text-slate-500">
                  <strong>How to read this:</strong> <span className="text-rose-700">High severity</span> = candidate has filled MORE entries than the package allows (these were blocked by the buggy save endpoint; after deploying the May 7 fix they can save again but will need to remove extras before submitting).
                  <span className="text-amber-700"> Medium severity</span> = candidate uploaded docs but has zero employment/education entries despite the package requiring them — they may not have completed the form yet, or their save failed silently. Reach out to high-severity candidates first.
                </div>
              </div>
            )}
          </CardContent>
        </Card>


        {/* Orphan Cases Scan + Heal — May 11 client-portal bulk-import bug */}
        <Card className="mb-6 border-orange-200" data-testid="orphan-cases-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Orphan Cases Scan &amp; Heal
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Surfaces verification cases where denormalized fields (candidate name, client, entity) are missing or "N/A" but the linked employee chain still resolves. Symptom: case appears in Drafts / Quick Assign but is missing from "All Cases" and shows N/A in Submitted columns. Heal backfills the fields from <code>employee → entity → client</code> and writes an audit trail entry per case.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <Label className="text-xs">Lookback (days)</Label>
                <Input
                  data-testid="orphan-days-input"
                  type="number"
                  min="1"
                  max="365"
                  value={orphanDays}
                  onChange={(e) => setOrphanDays(e.target.value)}
                  placeholder="30"
                />
              </div>
            </div>
            <div className="flex gap-2 mb-2 flex-wrap">
              <Button
                data-testid="orphan-scan-btn"
                onClick={runOrphanScan}
                disabled={orphanLoading}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {orphanLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Scan Orphan Cases
              </Button>
              {orphanResult && orphanResult.healable_count > 0 && (
                <>
                  <Button
                    data-testid="orphan-select-healable-btn"
                    onClick={selectAllHealable}
                    variant="outline"
                    size="sm"
                  >
                    Select all {orphanResult.healable_count} healable
                  </Button>
                  <Button
                    data-testid="orphan-dry-heal-btn"
                    onClick={() => runOrphanHeal(true)}
                    disabled={orphanHealing || orphanSelected.size === 0}
                    variant="outline"
                    size="sm"
                    className="border-blue-300 text-blue-700 disabled:opacity-40"
                  >
                    Dry Run ({orphanSelected.size})
                  </Button>
                  <Button
                    data-testid="orphan-heal-btn"
                    onClick={() => runOrphanHeal(false)}
                    disabled={orphanHealing || orphanSelected.size === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                  >
                    {orphanHealing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                    Heal Selected ({orphanSelected.size})
                  </Button>
                </>
              )}
            </div>

            {orphanResult && (
              <div className="mt-4 space-y-3">
                <div className="p-3 bg-orange-50 rounded border text-sm">
                  <p className="text-orange-900">
                    <strong>{orphanResult.count}</strong> orphan case(s) found in the last <strong>{orphanResult.scanned_days}</strong> days · <strong>{orphanResult.healable_count}</strong> healable.
                  </p>
                  <p className="text-xs text-slate-600 mt-1">{orphanResult.explanation}</p>
                </div>

                {orphanResult.count > 0 && (
                  <div className="border rounded max-h-[480px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 sticky top-0">
                          <TableHead className="w-8 text-xs"></TableHead>
                          <TableHead className="text-xs">Case ID</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                          <TableHead className="text-xs">Blank Fields</TableHead>
                          <TableHead className="text-xs">Expected Candidate</TableHead>
                          <TableHead className="text-xs">Expected Client / Entity</TableHead>
                          <TableHead className="text-xs">Created</TableHead>
                          <TableHead className="text-xs">Healable</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orphanResult.rows.map((r) => (
                          <TableRow key={r.case_id} data-testid={`orphan-row-${r.case_id}`}>
                            <TableCell>
                              {r.healable ? (
                                <input
                                  type="checkbox"
                                  data-testid={`orphan-select-${r.case_id}`}
                                  checked={orphanSelected.has(r.case_id)}
                                  onChange={() => toggleOrphanSelect(r.case_id)}
                                  className="cursor-pointer"
                                />
                              ) : null}
                            </TableCell>
                            <TableCell className="text-xs font-mono">{r.case_id}</TableCell>
                            <TableCell className="text-xs">{r.status}</TableCell>
                            <TableCell className="text-[10px]">
                              <div className="flex flex-wrap gap-0.5">
                                {(r.blank_fields || []).map((f, i) => (
                                  <Badge key={i} variant="outline" className="border-orange-300 text-orange-700 bg-orange-50 text-[9px]">
                                    {f}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs">
                              <div className="font-medium">{r.expected?.candidate_name || '—'}</div>
                              <div className="text-slate-500 text-[10px]">{r.expected?.candidate_email || ''}</div>
                            </TableCell>
                            <TableCell className="text-xs">
                              <div>{r.expected?.client_name || '—'}</div>
                              <div className="text-slate-500 text-[10px]">{r.expected?.entity_name || ''}</div>
                            </TableCell>
                            <TableCell className="text-[10px] font-mono">{(r.created_at || '').substring(0, 19)}</TableCell>
                            <TableCell>
                              {r.healable ? (
                                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Yes</Badge>
                              ) : (
                                <Badge variant="outline" className="border-rose-300 text-rose-700 text-[10px]" title={r.reason_not_healable}>
                                  No
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <div className="text-[11px] text-slate-500">
                  <strong>How to use:</strong> Click <em>Select all healable</em> to tick every case with a resolvable employee chain. Run <em>Dry Run</em> first to preview the patch payload per case. Then <em>Heal Selected</em> writes the backfill plus an audit trail entry. Non-healable rows (no employee link) need manual investigation — open the case and re-link the candidate, or delete it as a duplicate.
                </div>
              </div>
            )}
          </CardContent>
        </Card>


        {/* Missing User Accounts Scan + Heal — May 11 production auth fallout */}
        <Card className="mb-6 border-purple-200" data-testid="missing-users-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-purple-600" />
              Missing User Accounts Scan &amp; Heal
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Surfaces candidates visible in the employees collection but missing from <code>users</code>. Symptom: admin clicks Reset Password → toast shows temp password, but the candidate cannot log in (activity log says <code>user_not_found</code>). Heal creates a fresh <code>users</code> row with a temp password the admin can share.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <Label className="text-xs">Lookback (days, 0 = all time)</Label>
                <Input
                  data-testid="missing-days-input"
                  type="number"
                  min="0"
                  max="365"
                  value={missingDays}
                  onChange={(e) => setMissingDays(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="flex gap-2 mb-2 flex-wrap">
              <Button
                data-testid="missing-scan-btn"
                onClick={runMissingUserScan}
                disabled={missingLoading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {missingLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Scan Missing Users
              </Button>
              {missingResult && missingResult.count > 0 && (
                <>
                  <Button
                    data-testid="missing-select-all-btn"
                    onClick={() => setMissingSelected(new Set((missingResult.rows || []).map(r => r.employee_id)))}
                    variant="outline"
                    size="sm"
                  >
                    Select all {missingResult.count}
                  </Button>
                  <Button
                    data-testid="missing-dry-heal-btn"
                    onClick={() => runMissingUserHeal(true)}
                    disabled={missingHealing || missingSelected.size === 0}
                    variant="outline"
                    size="sm"
                    className="border-blue-300 text-blue-700 disabled:opacity-40"
                  >
                    Dry Run ({missingSelected.size})
                  </Button>
                  <Button
                    data-testid="missing-heal-btn"
                    onClick={() => runMissingUserHeal(false)}
                    disabled={missingHealing || missingSelected.size === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                  >
                    {missingHealing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                    Create Accounts ({missingSelected.size})
                  </Button>
                </>
              )}
            </div>

            {missingResult && (
              <div className="mt-4 space-y-3">
                <div className="p-3 bg-purple-50 rounded border text-sm">
                  <p className="text-purple-900">
                    <strong>{missingResult.count}</strong> candidate(s) without user accounts found · scanned <strong>{missingResult.total_employees_scanned}</strong> employees in the last <strong>{missingResult.scanned_days === 0 ? 'all-time' : `${missingResult.scanned_days}-day`}</strong> window.
                  </p>
                  <p className="text-xs text-slate-600 mt-1">{missingResult.explanation}</p>
                </div>

                {missingResult.count > 0 && (
                  <div className="border rounded max-h-[480px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 sticky top-0">
                          <TableHead className="w-8 text-xs"></TableHead>
                          <TableHead className="text-xs">Candidate</TableHead>
                          <TableHead className="text-xs">Email</TableHead>
                          <TableHead className="text-xs">Created</TableHead>
                          <TableHead className="text-xs">Employee ID</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {missingResult.rows.map((r) => (
                          <TableRow key={r.employee_id} data-testid={`missing-row-${r.employee_id}`}>
                            <TableCell>
                              <input
                                type="checkbox"
                                data-testid={`missing-select-${r.employee_id}`}
                                checked={missingSelected.has(r.employee_id)}
                                onChange={() => toggleMissingSelect(r.employee_id)}
                                className="cursor-pointer"
                              />
                            </TableCell>
                            <TableCell className="text-xs font-medium">{r.name || '—'}</TableCell>
                            <TableCell className="text-xs">{r.email}</TableCell>
                            <TableCell className="text-[10px] font-mono">{(r.created_at || '').substring(0, 19)}</TableCell>
                            <TableCell className="text-[10px] font-mono text-slate-500">{r.employee_id}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {missingHealResult && (missingHealResult.details || []).length > 0 && (
                  <div className="border rounded p-3 bg-emerald-50">
                    <p className="text-xs font-semibold text-emerald-900 mb-2">
                      {missingHealResult.dry_run ? 'Dry-run preview' : 'Created accounts — share these with the candidates'}
                    </p>
                    <div className="max-h-[300px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-emerald-100">
                            <TableHead className="text-xs">Email</TableHead>
                            <TableHead className="text-xs">Name</TableHead>
                            <TableHead className="text-xs">Temp Password</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {missingHealResult.details.map((d, i) => (
                            <TableRow key={i} data-testid={`missing-result-row-${i}`}>
                              <TableCell className="text-xs">{d.email || '—'}</TableCell>
                              <TableCell className="text-xs">{d.name || '—'}</TableCell>
                              <TableCell className="text-xs font-mono font-bold text-purple-700">
                                {d.temp_password || '—'}
                              </TableCell>
                              <TableCell className="text-xs">
                                <Badge
                                  variant="outline"
                                  className={
                                    d.status === 'healed' || d.status === 'would_heal'
                                      ? 'border-emerald-300 text-emerald-700'
                                      : d.status === 'skipped'
                                      ? 'border-amber-300 text-amber-700'
                                      : 'border-rose-300 text-rose-700'
                                  }
                                >
                                  {d.status}
                                </Badge>
                                {d.reason && <span className="ml-1 text-[10px] text-slate-500">{d.reason}</span>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-2">
                      <strong>Important:</strong> These temp passwords are shown ONCE. Copy them now or paste into a secure share with the candidate.
                    </p>
                  </div>
                )}

                <div className="text-[11px] text-slate-500">
                  <strong>How to use:</strong> Scan to find candidates missing their auth row. Select all (or specific ones), run <em>Dry Run</em> to preview temp passwords without committing, then <em>Create Accounts</em> to commit. Each candidate gets <code>force_password_change=True</code> so the temp password is a one-time use — they'll be prompted to set their own on first login.
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Empty Case Checks Recovery — May 14 production case stuck on Draft */}
        <Card className="mb-6 border-rose-200" data-testid="empty-checks-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-600" />
              Empty Case Checks Recovery
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Recover cases where <code>verification_cases.checks = []</code>. Symptom: candidate logs in, fills details, signs declaration; Review &amp; Submit shows <em>"Your Verification Package: Loading…"</em> and <em>"No verification checks configured"</em>; <em>"Cannot Submit Yet"</em> banner; no submission email; audit trail stuck on <em>Case Created</em>. Heal rebuilds checks from the resolved package (chain: <code>case.package_id</code> → <code>entity.package_id</code> → <code>employee.package_id</code> → <code>case.custom_checks</code>).
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-xs">Case ID</Label>
                <Input
                  data-testid="empty-checks-case-id-input"
                  value={emptyChecksCaseId}
                  onChange={(e) => setEmptyChecksCaseId(e.target.value)}
                  placeholder="e.g. ASC050526_01084"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Package ID Override (optional)</Label>
                <Input
                  data-testid="empty-checks-pkg-override-input"
                  value={emptyChecksOverride}
                  onChange={(e) => setEmptyChecksOverride(e.target.value)}
                  placeholder="Leave blank to auto-resolve via fallback chain"
                />
              </div>
            </div>
            <div className="flex gap-2 mb-3 flex-wrap">
              <Button
                data-testid="empty-checks-inspect-btn"
                onClick={runEmptyChecksScan}
                disabled={emptyChecksScanLoading}
                variant="outline"
              >
                {emptyChecksScanLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Inspect Case
              </Button>
              <Button
                data-testid="empty-checks-dry-run-btn"
                onClick={() => runEmptyChecksHeal(true)}
                disabled={emptyChecksHealLoading || !emptyChecksScan?.broken}
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                {emptyChecksHealLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Dry Run Heal
              </Button>
              <Button
                data-testid="empty-checks-apply-btn"
                onClick={() => runEmptyChecksHeal(false)}
                disabled={emptyChecksHealLoading || !emptyChecksScan?.broken}
                className="bg-rose-600 hover:bg-rose-700"
              >
                Apply Heal
              </Button>
              <Button
                data-testid="empty-checks-global-scan-btn"
                onClick={runEmptyChecksGlobalScan}
                disabled={emptyChecksGlobalLoading}
                variant="ghost"
                className="ml-auto"
              >
                {emptyChecksGlobalLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Global scan (find all broken)
              </Button>
            </div>

            {emptyChecksScan && (
              <div className={`rounded-lg border p-3 mb-3 ${emptyChecksScan.broken ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="empty-checks-inspect-result">
                <div className="text-sm font-semibold mb-2">
                  {emptyChecksScan.broken ? 'BROKEN — empty checks array' : 'OK — case has populated checks'}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div><strong>Status:</strong> {emptyChecksScan.case?.status}</div>
                  <div><strong>Checks:</strong> {emptyChecksScan.case?.checks_count}</div>
                  <div><strong>Submitted:</strong> {emptyChecksScan.case?.employee_submitted ? 'yes' : 'no'}</div>
                  <div><strong>Has custom_checks:</strong> {emptyChecksScan.case?.has_custom_checks ? `yes (${emptyChecksScan.case?.custom_checks_count})` : 'no'}</div>
                  <div className="md:col-span-2"><strong>case.package_id:</strong> <code className="break-all">{emptyChecksScan.case?.package_id || '(none)'}</code></div>
                  <div className="md:col-span-2"><strong>entity.package_id:</strong> <code className="break-all">{emptyChecksScan.entity?.package_id || '(none)'}</code></div>
                </div>
              </div>
            )}

            {emptyChecksPreview && (
              <div className={`rounded-lg border p-3 mb-3 ${emptyChecksPreview.applied ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`} data-testid="empty-checks-preview">
                <div className="text-sm font-semibold mb-2">
                  {emptyChecksPreview.applied
                    ? `Applied — ${emptyChecksPreview.proposed_checks_count} check(s) written`
                    : `Dry Run — would add ${emptyChecksPreview.proposed_checks_count} check(s)`}
                </div>
                <div className="text-xs space-y-1">
                  <div><strong>Resolved package:</strong> {emptyChecksPreview.resolved_package_name} (<code className="break-all">{emptyChecksPreview.resolved_package_id}</code>)</div>
                  <div><strong>Resolution chain:</strong></div>
                  <ul className="list-disc ml-5">
                    {(emptyChecksPreview.resolution_steps || []).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                  <details className="mt-2">
                    <summary className="cursor-pointer font-semibold">Proposed checks ({emptyChecksPreview.proposed_checks_count})</summary>
                    <pre className="text-[10px] bg-white p-2 rounded mt-1 overflow-auto max-h-64">{JSON.stringify(emptyChecksPreview.proposed_checks, null, 2)}</pre>
                  </details>
                </div>
              </div>
            )}

            {emptyChecksGlobal && (
              <div className="rounded-lg border bg-slate-50 p-3 mb-1" data-testid="empty-checks-global-result">
                <div className="text-sm font-semibold mb-2">Global scan: {emptyChecksGlobal.total_broken} broken case(s)</div>
                {emptyChecksGlobal.total_broken > 0 && (
                  <div className="max-h-64 overflow-auto">
                    <table className="text-xs w-full">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left p-1">Case ID</th>
                          <th className="text-left p-1">Candidate</th>
                          <th className="text-left p-1">Status</th>
                          <th className="text-left p-1">Submitted</th>
                          <th className="text-left p-1">Created</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {emptyChecksGlobal.cases.map((c) => (
                          <tr key={c.id} className="border-b">
                            <td className="p-1"><code>{c.id}</code></td>
                            <td className="p-1">{c.candidate_name || '-'}</td>
                            <td className="p-1">{c.status}</td>
                            <td className="p-1">{c.employee_submitted ? 'yes' : 'no'}</td>
                            <td className="p-1">{(c.created_at || '').slice(0, 10)}</td>
                            <td className="p-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setEmptyChecksCaseId(c.id); setTimeout(runEmptyChecksScan, 0); }}
                              >
                                Load
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="text-[11px] text-slate-500 mt-2">
              <strong>How to use:</strong> Paste the case ID → <em>Inspect Case</em>. If broken, click <em>Dry Run Heal</em> to preview the rebuilt checks array. Review the resolution chain + proposed checks, then click <em>Apply Heal</em> to write to the case. Safety: Apply refuses to run if the case already has checks.
            </div>
          </CardContent>
        </Card>

        {/* Repair Case Rollup — May 24, 2026 — fixes "Draft" stuck cases */}
        <Card className="mb-6 border-emerald-200" data-testid="repair-rollup-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-emerald-600" />
              Repair Case Rollup
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              For cases stuck at <strong>"Draft"</strong> / <code className="text-xs bg-slate-100 px-1">report_pending_qc</code> on the QC dashboard despite all checks being verified (typically after a Remove/Reset Check that didn't propagate). Re-runs the canonical status rollup helper to set <strong>status, overall_result, completed_at</strong> from the underlying checks. Optionally re-sends the completion-report email with the corrected subject (e.g., "Final Report - GREEN" instead of "PENDING"). Read-only preview is shown first.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3 items-center flex-wrap">
              <Input
                data-testid="repair-rollup-case-input"
                placeholder="Enter Case ID (e.g. VER300426_01005)"
                value={repairCaseId}
                onChange={(e) => setRepairCaseId(e.target.value)}
                className="max-w-sm font-mono text-sm"
                onKeyDown={(e) => e.key === 'Enter' && previewCaseRollupRepair()}
              />
              <Button
                data-testid="repair-rollup-preview-btn"
                onClick={previewCaseRollupRepair}
                disabled={repairRollupLoading}
                variant="outline"
              >
                {repairRollupLoading ? 'Loading…' : 'Preview'}
              </Button>
              <label className="text-xs flex items-center gap-1 ml-2">
                <input
                  type="checkbox"
                  data-testid="repair-rollup-resend-toggle"
                  checked={repairResendEmail}
                  onChange={(e) => setRepairResendEmail(e.target.checked)}
                />
                Also re-send completion report email
              </label>
            </div>

            {repairPreview && (
              <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-4 mt-2" data-testid="repair-rollup-preview">
                <div className="text-sm font-semibold text-emerald-900 mb-2">Preview — current vs computed:</div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-white rounded p-2 border">
                    <div className="font-semibold text-slate-700 mb-1">Current (stuck)</div>
                    <div>status: <code>{repairPreview.preview?.current?.status || '-'}</code></div>
                    <div>verification_progress: <code>{repairPreview.preview?.current?.verification_progress || '-'}</code></div>
                    <div>overall_result: <code>{repairPreview.preview?.current?.overall_result || '-'}</code></div>
                    <div>completed_at: <code>{repairPreview.preview?.current?.completed_at || '-'}</code></div>
                    <div>completion_email_sent: <code>{String(repairPreview.preview?.current?.completion_email_sent)}</code></div>
                  </div>
                  <div className="bg-white rounded p-2 border">
                    <div className="font-semibold text-emerald-700 mb-1">Computed (after repair)</div>
                    <div>verification_progress: <code>{repairPreview.preview?.computed?.verification_progress || '-'}</code></div>
                    <div>overall_result: <code>{repairPreview.preview?.computed?.overall_result || '-'}</code></div>
                    <div>last_completed_check: <code>{repairPreview.preview?.computed?.last_completed_check_iso || '-'}</code></div>
                    <div>checks_count: <code>{repairPreview.preview?.computed?.checks_count}</code></div>
                    <div>all_terminal: <code>{String(repairPreview.preview?.computed?.all_terminal)}</code></div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button
                    data-testid="repair-rollup-apply-btn"
                    onClick={applyCaseRollupRepair}
                    disabled={repairRollupLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {repairRollupLoading ? 'Applying…' : 'Apply Repair'}
                  </Button>
                  <Button
                    data-testid="repair-rollup-cancel-btn"
                    onClick={() => setRepairPreview(null)}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {repairResult && (
              <div className="rounded-lg border-2 border-emerald-400 bg-white p-3 mt-3" data-testid="repair-rollup-result">
                <div className="text-sm font-semibold text-emerald-800 mb-1">✓ Repair applied</div>
                <div className="text-xs text-slate-700">{repairResult.message}</div>
                {repairResult.after && (
                  <div className="text-xs mt-2 grid grid-cols-2 gap-1">
                    <div>status: <code>{repairResult.after.status}</code></div>
                    <div>verification_progress: <code>{repairResult.after.verification_progress}</code></div>
                    <div>overall_result: <code>{repairResult.after.overall_result}</code></div>
                    <div>date_completed: <code>{repairResult.after.date_completed || repairResult.after.completed_at || '-'}</code></div>
                  </div>
                )}
                {repairResult.email_resent && (
                  <div className="text-xs text-emerald-700 mt-1">✉ Completion-report email re-sent.</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Address Sub-check Mismatch Repair — May 30, 2026 — fixes recurring present/permanent mix-up */}
        <Card className="mb-6 border-rose-300" data-testid="addr-mismatch-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="h-5 w-5 text-rose-600" />
              Address Sub-check Data Mismatch
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Finds <strong>address_check</strong> rows whose <code className="text-xs bg-slate-100 px-1">subcheck_data</code> disagrees with the row's <code className="text-xs bg-slate-100 px-1">sub_option</code> (e.g. a Permanent row carrying Present-side address payload). This is the recurring data corruption behind the AD-XXXXX-2 present/permanent vendor-email bug. Read-time fix is already live, but this tool permanently cleans the bad rows. <strong>You pick which to repair</strong> — rows you've already manually fixed stay untouched.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 mb-3 items-center flex-wrap">
              <Button
                data-testid="addr-mismatch-scan-btn"
                onClick={scanAddrMismatch}
                disabled={addrMismatchLoading}
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                {addrMismatchLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Scan for Mismatches
              </Button>
              <label className="text-xs flex items-center gap-1">
                <input
                  type="checkbox"
                  data-testid="addr-mismatch-include-completed"
                  checked={addrMismatchIncludeCompleted}
                  onChange={(e) => setAddrMismatchIncludeCompleted(e.target.checked)}
                />
                Include completed / verified checks
              </label>
              <div className="text-xs text-slate-500">
                Repair mode:
                <select
                  data-testid="addr-mismatch-mode-select"
                  value={addrMismatchMode}
                  onChange={(e) => setAddrMismatchMode(e.target.value)}
                  className="ml-2 border rounded px-2 py-1 text-xs"
                >
                  <option value="rewrite_to_canonical">Rewrite subcheck_data to canonical address</option>
                  <option value="clear">Clear subcheck_data (fall back to employee fields)</option>
                </select>
              </div>
            </div>

            {addrMismatchFindings && (
              <div className="rounded-lg border-2 border-rose-300 bg-rose-50/40 p-3 mt-2" data-testid="addr-mismatch-results">
                <div className="text-sm font-semibold text-rose-900 mb-2 flex items-center justify-between">
                  <span>
                    {addrMismatchFindings.total_found} mismatched check{addrMismatchFindings.total_found === 1 ? '' : 's'} found
                    {addrMismatchFindings.limit_hit && <span className="text-xs text-rose-600 ml-2">(limit hit — increase scan limit if needed)</span>}
                  </span>
                  {addrMismatchFindings.findings.length > 0 && (
                    <div className="flex gap-2 items-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={toggleAddrMismatchAll}
                        data-testid="addr-mismatch-select-all"
                      >
                        {addrMismatchFindings.findings.every((f) => addrMismatchSelected[`${f.case_id}::${f.check_id}`]) ? 'Unselect all' : 'Select all'}
                      </Button>
                      <Button
                        size="sm"
                        className="bg-rose-600 hover:bg-rose-700"
                        onClick={repairSelectedAddrMismatch}
                        disabled={addrMismatchRepairLoading || Object.values(addrMismatchSelected).filter(Boolean).length === 0}
                        data-testid="addr-mismatch-repair-btn"
                      >
                        {addrMismatchRepairLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wrench className="h-4 w-4 mr-1" />}
                        Repair Selected ({Object.values(addrMismatchSelected).filter(Boolean).length})
                      </Button>
                    </div>
                  )}
                </div>

                {addrMismatchFindings.findings.length === 0 ? (
                  <div className="text-xs text-slate-600 py-3 text-center">
                    ✓ No mismatched address sub-checks detected. All rows are clean.
                  </div>
                ) : (
                  <div className="overflow-x-auto max-h-[500px] overflow-y-auto bg-white rounded border">
                    <Table>
                      <TableHeader className="bg-rose-100 sticky top-0">
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead className="text-xs">Case</TableHead>
                          <TableHead className="text-xs">Check ID</TableHead>
                          <TableHead className="text-xs">Side</TableHead>
                          <TableHead className="text-xs">Candidate</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                          <TableHead className="text-xs">Mismatch Reason</TableHead>
                          <TableHead className="text-xs">subcheck_data</TableHead>
                          <TableHead className="text-xs">Canonical (correct)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {addrMismatchFindings.findings.map((f) => {
                          const key = `${f.case_id}::${f.check_id}`;
                          const isSel = !!addrMismatchSelected[key];
                          return (
                            <TableRow key={key} className={isSel ? 'bg-rose-50' : ''} data-testid={`addr-mismatch-row-${f.check_id}`}>
                              <TableCell>
                                <Checkbox
                                  checked={isSel}
                                  onCheckedChange={(v) => setAddrMismatchSelected({ ...addrMismatchSelected, [key]: !!v })}
                                  data-testid={`addr-mismatch-checkbox-${f.check_id}`}
                                />
                              </TableCell>
                              <TableCell className="text-xs font-mono">{f.case_id_display}</TableCell>
                              <TableCell className="text-xs font-mono font-bold text-rose-700">{f.check_id}</TableCell>
                              <TableCell className="text-xs">
                                <Badge variant="outline" className={f.sub_option === 'permanent' ? 'border-purple-300 text-purple-700' : 'border-blue-300 text-blue-700'}>
                                  {f.sub_option}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs">{f.candidate_name}</TableCell>
                              <TableCell className="text-xs">
                                <Badge variant="outline" className="text-[10px]">{f.status}</Badge>
                              </TableCell>
                              <TableCell className="text-xs">
                                <ul className="list-disc pl-3 space-y-0.5">
                                  {(f.reasons || []).map((r, i) => (
                                    <li key={i} className="text-rose-700">{r}</li>
                                  ))}
                                </ul>
                              </TableCell>
                              <TableCell className="text-xs">
                                <div className="text-rose-600">
                                  <div>{f.subcheck_data_preview?.city}, {f.subcheck_data_preview?.state}</div>
                                  <div className="text-slate-500">pin: {f.subcheck_data_preview?.pincode || '—'}</div>
                                  {f.subcheck_data_preview?.complete_address && (
                                    <div className="text-[10px] text-slate-400 truncate max-w-[180px]" title={f.subcheck_data_preview.complete_address}>
                                      {f.subcheck_data_preview.complete_address}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-xs">
                                <div className="text-emerald-700">
                                  <div>{f.canonical_for_side?.city}, {f.canonical_for_side?.state}</div>
                                  <div className="text-slate-500">pin: {f.canonical_for_side?.pincode || '—'}</div>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            {addrMismatchResult && (
              <div className="rounded-lg border-2 border-emerald-400 bg-white p-3 mt-3" data-testid="addr-mismatch-result">
                <div className="text-sm font-semibold text-emerald-800 mb-1">✓ Repair complete</div>
                <div className="text-xs text-slate-700 grid grid-cols-4 gap-2">
                  <div>Total targets: <strong>{addrMismatchResult.total_targets}</strong></div>
                  <div>Repaired: <strong className="text-emerald-700">{addrMismatchResult.repaired}</strong></div>
                  <div>Skipped: <strong className="text-amber-700">{addrMismatchResult.skipped}</strong></div>
                  <div>Failed: <strong className="text-rose-700">{addrMismatchResult.failed}</strong></div>
                </div>
                {addrMismatchResult.details && addrMismatchResult.details.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-slate-600 cursor-pointer">Per-target details</summary>
                    <div className="text-[11px] mt-1 max-h-60 overflow-y-auto bg-slate-50 p-2 rounded">
                      {addrMismatchResult.details.map((d, i) => (
                        <div key={i} className="mb-1">
                          <strong>{d.target?.check_id}</strong>: {d.outcome}
                          {d.reason && <span className="text-slate-500"> — {d.reason}</span>}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}

            <div className="text-[11px] text-slate-500 mt-2">
              <strong>How to use:</strong> Click <em>Scan</em> → review the list → tick the rows you actually want to repair (skip any you've already corrected manually) → click <em>Repair Selected</em>. The repair is logged on the check's <code className="bg-slate-100 px-1">audit_log</code>. <strong>Rewrite mode</strong> sets subcheck_data to the canonical employee address for that side; <strong>Clear mode</strong> wipes subcheck_data so reads fall through to the employee record.
            </div>

            {/* Per-case diagnostic — May 31, 2026 */}
            <div className="mt-4 pt-4 border-t border-rose-100">
              <div className="text-sm font-semibold text-rose-900 mb-1 flex items-center gap-2">
                <Search className="h-4 w-4" />
                Diagnose a specific case
              </div>
              <div className="text-xs text-slate-500 mb-2">
                Paste a case ID (e.g. <code className="bg-slate-100 px-1">SOL290526_01649</code>) to see the raw fields stored on its address checks. Useful when the scan returns 0 but you suspect a specific case is mis-rendering.
              </div>
              <div className="flex gap-2 flex-wrap items-end">
                <div>
                  <Label className="text-xs">Case ID</Label>
                  <Input
                    value={diagCaseId}
                    onChange={(e) => setDiagCaseId(e.target.value)}
                    placeholder="SOL290526_01649"
                    className="w-64 h-9 font-mono text-xs"
                    data-testid="addr-diag-case-id"
                  />
                </div>
                <div>
                  <Label className="text-xs">Check ID (optional)</Label>
                  <Input
                    value={diagCheckId}
                    onChange={(e) => setDiagCheckId(e.target.value)}
                    placeholder="AD-05957-2"
                    className="w-48 h-9 font-mono text-xs"
                    data-testid="addr-diag-check-id"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runAddrDiagnostic}
                  disabled={addrDiagLoading || !diagCaseId.trim()}
                  className="border-rose-300 text-rose-700 hover:bg-rose-50 h-9"
                  data-testid="addr-diag-run-btn"
                >
                  {addrDiagLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                  Diagnose
                </Button>
              </div>

              {diagResult && (
                <div className="mt-3 rounded border border-rose-200 bg-white p-3" data-testid="addr-diag-result">
                  <div className="text-xs font-semibold text-slate-700 mb-1">
                    Case: <span className="font-mono">{diagResult.case_id_display || diagResult.case_id}</span>
                    {' — '}
                    Employee: <span className="font-mono">{diagResult.employee?.name || '—'}</span>
                  </div>
                  <div className="text-[11px] text-slate-600 mb-2">
                    Canonical Present: {diagResult.employee?.present_city_flat || '—'} / {diagResult.employee?.present_pincode_flat || '—'} &nbsp;|&nbsp;
                    Canonical Permanent: {diagResult.employee?.permanent_city_flat || '—'} / {diagResult.employee?.permanent_pincode_flat || '—'}
                  </div>
                  <div className="space-y-2">
                    {(diagResult.address_checks || []).map((c, i) => (
                      <div key={i} className="rounded border bg-slate-50 p-2 text-[11px]">
                        <div className="flex flex-wrap gap-3">
                          <span><strong>check_id:</strong> {c.check_id}-{c.check_index}</span>
                          <span><strong>sub_option:</strong> <code className={c.scan_helper_reasons.length > 0 ? 'text-rose-600 font-bold' : 'text-emerald-700'}>{c.sub_option}</code></span>
                          <span><strong>status:</strong> {c.status}</span>
                          <span><strong>top-level address_type:</strong> <code>{c.top_level_address_type ?? 'null'}</code></span>
                          <span><strong>subcheck_data present:</strong> {c.subcheck_data_present ? 'yes' : 'no'}</span>
                        </div>
                        {c.subcheck_data && Object.keys(c.subcheck_data).length > 0 && (
                          <div className="mt-1">
                            <strong>subcheck_data.address_type:</strong> <code>{c.subcheck_data.address_type ?? 'null'}</code>{' '}
                            | <strong>city/state/pin:</strong> {c.subcheck_data.city || '—'} / {c.subcheck_data.state || '—'} / {c.subcheck_data.pincode || '—'}
                          </div>
                        )}
                        {c.scan_helper_reasons && c.scan_helper_reasons.length > 0 ? (
                          <div className="mt-1 text-rose-700">
                            <strong>Mismatch reasons:</strong>
                            <ul className="list-disc pl-4">
                              {c.scan_helper_reasons.map((r, ri) => <li key={ri}>{r}</li>)}
                            </ul>
                          </div>
                        ) : (
                          <div className="mt-1 text-emerald-700"><strong>✓ Scan helper sees this row as clean.</strong></div>
                        )}
                      </div>
                    ))}
                  </div>
                  <details className="mt-2">
                    <summary className="text-[11px] text-slate-500 cursor-pointer">Show full raw JSON</summary>
                    <pre className="text-[10px] bg-slate-100 p-2 mt-1 rounded overflow-x-auto">{JSON.stringify(diagResult, null, 2)}</pre>
                  </details>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Revert Wrongly-Migrated Cases — May 24, 2026 — undo bad Basic-Initiated migrations */}
        <Card className="mb-6 border-amber-300" data-testid="revert-wrong-migration-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-600" />
              Revert Wrongly-Migrated Cases
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              For cases that were <strong>incorrectly moved to "Draft" (basic_initiated)</strong> by the Basic-Initiated migration tool even though the candidate had already submitted the form or QC had raised an insufficiency. Restores them to their original status (typically <code>in_progress</code>). Selection criteria: <code>migrated_from_status</code> exists AND <em>(employee_submitted=true OR has_open_insufficiency=true OR any check has_insufficiency=true)</em>.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3">
              <Button
                data-testid="revert-wrong-preview-btn"
                onClick={previewRevertWrong}
                disabled={revertWrongLoading}
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                {revertWrongLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
                Preview
              </Button>
            </div>

            {revertWrongPreview && (
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 mt-2" data-testid="revert-wrong-preview">
                <div className="text-sm font-semibold text-amber-900 mb-2">
                  {revertWrongPreview.count} wrongly-migrated case{revertWrongPreview.count === 1 ? '' : 's'} found
                </div>
                {revertWrongPreview.will_revert_to && (
                  <div className="text-xs mb-2">
                    Will revert to: {Object.entries(revertWrongPreview.will_revert_to).map(([k, v]) => (
                      <span key={k} className="inline-block mr-3"><code>{k}</code>: <strong>{v}</strong></span>
                    ))}
                  </div>
                )}
                {revertWrongPreview.cases && revertWrongPreview.cases.length > 0 && (
                  <div className="bg-white rounded p-2 border max-h-48 overflow-auto text-xs">
                    {revertWrongPreview.cases.slice(0, 50).map(c => (
                      <div key={c.id} className="border-b py-1 flex justify-between">
                        <span><code>{c.id}</code> — {c.employee_name || '-'}</span>
                        <span className="text-slate-500">
                          → {c.migrated_from_status || 'in_progress'}
                          {c.employee_submitted && ' • submitted'}
                          {c.has_open_insufficiency && ' • insuff'}
                        </span>
                      </div>
                    ))}
                    {revertWrongPreview.cases.length > 50 && (
                      <div className="text-slate-500 italic mt-1">…and {revertWrongPreview.cases.length - 50} more</div>
                    )}
                  </div>
                )}
                {revertWrongPreview.count > 0 && (
                  <div className="flex gap-2 mt-3">
                    <Button
                      data-testid="revert-wrong-execute-btn"
                      onClick={executeRevertWrong}
                      disabled={revertWrongLoading}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {revertWrongLoading ? 'Reverting…' : `Revert ${revertWrongPreview.count} Case${revertWrongPreview.count === 1 ? '' : 's'}`}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setRevertWrongPreview(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            )}

            {revertWrongResult && (
              <div className="rounded-lg border-2 border-emerald-400 bg-white p-3 mt-3" data-testid="revert-wrong-result">
                <div className="text-sm font-semibold text-emerald-800 mb-1">✓ Revert applied</div>
                <div className="text-xs text-slate-700">{revertWrongResult.message}</div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reset Single Check — May 23, 2026 — for cases where return-to-ops-manager is stuck */}
        <Card className="mb-6 border-rose-200" data-testid="reset-check-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-rose-600" />
              Reset Single Check
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              For cases where <strong>Return to Ops Manager</strong> doesn't propagate (e.g., agent uploaded annexure, set verification result, but the check stays locked). Loads all checks on the case and lets you reset a single one — clears <strong>vendor assignment, agent assignment, verification form, uploaded documents (archived), completion date, TAT, agent remarks, field-form state, and any open insufficiency on that check</strong>. Other checks on the case stay untouched. The reset check becomes visible to Ops Manager for reassignment.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3">
              <Input
                data-testid="reset-check-case-input"
                placeholder="Enter Case ID (e.g. VER300426_01005)"
                value={rcaseInput}
                onChange={(e) => setRcaseInput(e.target.value)}
                className="max-w-sm font-mono text-sm"
                onKeyDown={(e) => e.key === 'Enter' && loadCaseChecksForReset()}
              />
              <Button
                data-testid="reset-check-load-btn"
                onClick={loadCaseChecksForReset}
                disabled={rcaseLoading}
                variant="outline"
              >
                {rcaseLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Load Checks
              </Button>
            </div>

            {rcaseSummary && (
              <div className="rounded-lg border bg-slate-50 p-3" data-testid="reset-check-summary">
                <div className="text-xs text-slate-600 mb-2">
                  <strong>{rcaseSummary.case_id}</strong> · {rcaseSummary.employee_name} · {rcaseSummary.client_name} · case status: <code>{rcaseSummary.case_status}</code> · {rcaseSummary.check_count} check(s)
                </div>
                <div className="max-h-72 overflow-auto">
                  <table className="text-xs w-full">
                    <thead className="sticky top-0 bg-slate-200">
                      <tr>
                        <th className="text-left p-1">Check ID</th>
                        <th className="text-left p-1">Type</th>
                        <th className="text-left p-1">Sub-option</th>
                        <th className="text-left p-1">Status</th>
                        <th className="text-left p-1">Vendor</th>
                        <th className="text-left p-1">Agent</th>
                        <th className="text-left p-1">Docs</th>
                        <th className="text-left p-1">Insuff?</th>
                        <th className="text-left p-1 w-24">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rcaseSummary.checks.map((c) => (
                        <tr key={c.check_id} className="border-b hover:bg-white" data-testid={`reset-check-row-${c.check_id}`}>
                          <td className="p-1 font-mono">{c.check_id}</td>
                          <td className="p-1">{c.check_type}</td>
                          <td className="p-1 text-slate-500">{c.sub_option || '-'}</td>
                          <td className="p-1"><code className={c.status === 'completed' ? 'text-emerald-700' : c.status === 'pending' ? 'text-slate-500' : 'text-amber-700'}>{c.status}</code></td>
                          <td className="p-1">{c.vendor_name || '-'}</td>
                          <td className="p-1">{c.assigned_to_name || '-'}</td>
                          <td className="p-1">{c.document_count}</td>
                          <td className="p-1">{c.has_insufficiency ? <span className="text-rose-700">yes</span> : '-'}</td>
                          <td className="p-1 flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                              data-testid={`reset-check-btn-${c.check_id}`}
                              onClick={() => previewCheckReset(c)}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Reset
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs border-red-400 text-red-700 hover:bg-red-50"
                              data-testid={`remove-check-btn-${c.check_id}`}
                              onClick={() => previewCheckRemove(c)}
                              title="Remove this check entirely from the case"
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {rcaseConfirm && (
              <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-4 mt-3" data-testid="reset-check-confirm">
                <div className="text-sm font-semibold text-rose-800 mb-2">⚠ Confirm reset of check <code>{rcaseConfirm.check.check_id}</code></div>
                <div className="text-xs text-rose-900 space-y-1">
                  <div>Current status: <strong>{rcaseConfirm.preview.current_status}</strong></div>
                  <div>Current vendor: <strong>{rcaseConfirm.preview.current_vendor || '-'}</strong></div>
                  <div>Current agent: <strong>{rcaseConfirm.preview.current_agent || '-'}</strong></div>
                  <div>Documents to archive: <strong>{rcaseConfirm.preview.documents_to_archive}</strong> (moved to <code>prior_documents</code>, recoverable)</div>
                  <div>Verification form data: <strong>{rcaseConfirm.preview.verified_details_to_clear ? 'will be cleared' : 'none present'}</strong></div>
                  <div>Field submission: <strong>{rcaseConfirm.preview.field_submission_to_clear ? 'will be cleared' : 'none present'}</strong></div>
                  <div>Insufficiency state: <strong>{rcaseConfirm.preview.had_insufficiency ? 'will be cleared + resolved' : 'none'}</strong></div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button
                    data-testid="reset-check-confirm-apply"
                    onClick={applyCheckReset}
                    disabled={rcaseResetting}
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    {rcaseResetting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
                    Confirm — Reset Check
                  </Button>
                  <Button
                    data-testid="reset-check-confirm-cancel"
                    onClick={() => setRcaseConfirm(null)}
                    variant="outline"
                    disabled={rcaseResetting}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {removeConfirm && (
              <div className="rounded-lg border-2 border-red-400 bg-red-50 p-4 mt-3" data-testid="remove-check-confirm">
                <div className="text-sm font-bold text-red-800 mb-2 flex items-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  Permanently remove check <code>{removeConfirm.check.check_id}</code>?
                </div>
                <div className="text-xs text-red-900 space-y-1">
                  <div>Type: <strong>{removeConfirm.check.check_type}</strong>{removeConfirm.check.sub_option ? ` (${removeConfirm.check.sub_option})` : ''}</div>
                  <div>Current status: <strong>{removeConfirm.preview.current_status}</strong></div>
                  {removeConfirm.preview.current_vendor && <div>Vendor: <strong>{removeConfirm.preview.current_vendor}</strong></div>}
                  {removeConfirm.preview.current_agent && <div>Agent: <strong>{removeConfirm.preview.current_agent}</strong></div>}
                  {typeof removeConfirm.preview.documents_count === 'number' && (
                    <div>Documents on this check: <strong>{removeConfirm.preview.documents_count}</strong></div>
                  )}
                  {typeof removeConfirm.preview.siblings_after_removal === 'number' && (
                    <div>Checks remaining on case after removal: <strong>{removeConfirm.preview.siblings_after_removal}</strong></div>
                  )}
                </div>

                <div className="mt-3 rounded-md bg-white border border-red-200 p-3 text-xs text-slate-700">
                  This will remove the check from <code>case.checks</code> and archive a full snapshot to <code>case.removed_checks[]</code>. The case overall status will be recomputed based on the remaining checks. The removal is recorded in <code>audit_logs</code> and is forensically recoverable.
                </div>

                {removeConfirm.preview.is_protected_state && (
                  <div className="mt-3 rounded-md border-2 border-amber-400 bg-amber-50 p-3" data-testid="remove-protected-warning">
                    <div className="text-xs font-semibold text-amber-900 mb-2">
                      ⚠ This check is in <strong>{removeConfirm.preview.current_status}</strong> state — removing it will <strong>discard verified work</strong>.
                    </div>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="remove-override-safety-toggle"
                        checked={removeOverride}
                        onChange={(e) => setRemoveOverride(e.target.checked)}
                      />
                      <span>I understand — <strong>Override safety</strong> and remove this verified check anyway.</span>
                    </label>
                  </div>
                )}

                <div className="mt-3">
                  <Label className="text-xs">Reason (required)</Label>
                  <Input
                    data-testid="remove-reason-input"
                    placeholder="e.g., Client requested to drop this check / orphaned by QA&A linkage bug"
                    value={removeReason}
                    onChange={(e) => setRemoveReason(e.target.value)}
                    className="mt-1 text-sm"
                  />
                </div>

                <div className="mt-3">
                  <Label className="text-xs">Type <code>REMOVE</code> to confirm</Label>
                  <Input
                    data-testid="remove-confirm-phrase-input"
                    placeholder="REMOVE"
                    value={removePhrase}
                    onChange={(e) => setRemovePhrase(e.target.value)}
                    className="mt-1 text-sm font-mono"
                  />
                </div>

                <div className="flex gap-2 mt-3">
                  <Button
                    data-testid="remove-check-confirm-apply"
                    onClick={applyCheckRemove}
                    disabled={
                      removing ||
                      (removePhrase || '').trim().toUpperCase() !== 'REMOVE' ||
                      !(removeReason || '').trim() ||
                      (removeConfirm.preview.is_protected_state && !removeOverride)
                    }
                    className="bg-red-600 hover:bg-red-700 disabled:opacity-60"
                  >
                    {removing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                    Confirm — Remove Check
                  </Button>
                  <Button
                    data-testid="remove-check-confirm-cancel"
                    onClick={() => { setRemoveConfirm(null); setRemovePhrase(''); setRemoveReason(''); setRemoveOverride(false); }}
                    variant="outline"
                    disabled={removing}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Mirror Address Backfill — May 21, 2026 — historical bulk-imported candidates whose addresses match but flag never set */}
        <Card className="mb-6 border-indigo-200" data-testid="mirror-backfill-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-indigo-600" />
              Address Mirror Backfill
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              For historical bulk-imported candidates whose present and permanent addresses are identical, but{' '}
              <code>permanent_same_as_present</code> was never set (because mirror auto-detect shipped after the import).
              Healing flips the flag so the permanent address check is auto-synced from present — eliminating duplicate
              vendor work going forward. Does <strong>not</strong> alter cases that have already been completed for the
              permanent check.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 mb-3 flex-wrap items-center">
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  data-testid="mirror-only-bulk-toggle"
                  checked={mirrorOnlyBulk}
                  onChange={(e) => setMirrorOnlyBulk(e.target.checked)}
                />
                Restrict to <code>source = bulk_import</code> only
              </label>
            </div>
            <div className="flex gap-2 mb-3 flex-wrap">
              <Button
                data-testid="mirror-scan-btn"
                onClick={runMirrorScan}
                disabled={mirrorScanLoading}
                variant="outline"
              >
                {mirrorScanLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Scan Candidates
              </Button>
              <Button
                data-testid="mirror-dry-run-btn"
                onClick={() => runMirrorHeal(true)}
                disabled={mirrorHealLoading || !mirrorScan?.match_count || mirrorSelected.size === 0}
                variant="outline"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              >
                {mirrorHealLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Dry Run Heal ({mirrorSelected.size})
              </Button>
              <Button
                data-testid="mirror-apply-btn"
                onClick={() => runMirrorHeal(false)}
                disabled={mirrorHealLoading || !mirrorScan?.match_count || mirrorSelected.size === 0}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {mirrorHealLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Apply Heal ({mirrorSelected.size})
              </Button>
            </div>

            {mirrorScan && (
              <div className="rounded-lg border bg-slate-50 p-3 mb-3" data-testid="mirror-scan-results">
                <div className="text-sm font-semibold mb-2">
                  {mirrorScan.match_count} candidate(s) eligible for mirror backfill
                </div>
                {mirrorScan.match_count > 0 && (
                  <div className="max-h-72 overflow-auto">
                    <table className="text-xs w-full">
                      <thead className="sticky top-0 bg-slate-200">
                        <tr>
                          <th className="text-left p-1 w-8">
                            <input
                              type="checkbox"
                              data-testid="mirror-select-all"
                              checked={mirrorSelected.size === mirrorScan.candidates.length && mirrorScan.candidates.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) setMirrorSelected(new Set(mirrorScan.candidates.map((c) => c.employee_id)));
                                else setMirrorSelected(new Set());
                              }}
                            />
                          </th>
                          <th className="text-left p-1">Employee</th>
                          <th className="text-left p-1">Email</th>
                          <th className="text-left p-1">Case ID</th>
                          <th className="text-left p-1">Source</th>
                          <th className="text-left p-1">Address</th>
                          <th className="text-left p-1">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mirrorScan.candidates.map((c) => (
                          <tr key={c.employee_id} className="border-b hover:bg-white">
                            <td className="p-1">
                              <input
                                type="checkbox"
                                data-testid={`mirror-row-${c.employee_id}`}
                                checked={mirrorSelected.has(c.employee_id)}
                                onChange={() => toggleMirrorSelected(c.employee_id)}
                              />
                            </td>
                            <td className="p-1">{c.name || '-'}</td>
                            <td className="p-1 break-all">{c.email}</td>
                            <td className="p-1"><code>{c.case_id || '-'}</code></td>
                            <td className="p-1">{c.source}</td>
                            <td className="p-1 max-w-xs truncate" title={c.address_summary}>{c.address_summary}</td>
                            <td className="p-1">{(c.created_at || '').slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {mirrorHealResult && (
              <div
                className={`rounded-lg border p-3 ${mirrorHealResult.dry_run ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`}
                data-testid="mirror-heal-result"
              >
                <div className="text-sm font-semibold">
                  {mirrorHealResult.dry_run
                    ? `Dry Run — would heal ${mirrorHealResult.would_heal_count}`
                    : `Healed ${mirrorHealResult.healed_count} candidate(s)`}
                  {mirrorHealResult.skipped_count > 0 && ` · skipped ${mirrorHealResult.skipped_count}`}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inactive Agent Assignment Sync — May 15 — auto-assign was picking deactivated agents */}
        <Card className="mb-6 border-amber-200" data-testid="inactive-agent-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Inactive Agent Assignment Sync
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Heal the legacy bug where deactivating a user via Team Management toggle didn't flip <code>agent_assignments.is_active</code>. Symptom: auto-approve assign hands ID/Address/Criminal checks to a deactivated agent. Forward-fix is shipped (deactivate now syncs both rows); this tool aligns prod rows that were already out of sync.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3 flex-wrap">
              <Button
                data-testid="inactive-agent-scan-btn"
                onClick={runInactiveAgentScan}
                disabled={inactiveAgentScanLoading}
                variant="outline"
              >
                {inactiveAgentScanLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Scan Mismatches
              </Button>
              <Button
                data-testid="inactive-agent-dry-run-btn"
                onClick={() => runInactiveAgentHeal(true)}
                disabled={inactiveAgentHealLoading || !inactiveAgentScan?.total_mismatches}
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                {inactiveAgentHealLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Dry Run Heal
              </Button>
              <Button
                data-testid="inactive-agent-apply-btn"
                onClick={() => runInactiveAgentHeal(false)}
                disabled={inactiveAgentHealLoading || !inactiveAgentScan?.total_mismatches}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Apply Heal
              </Button>
            </div>

            {inactiveAgentScan && (
              <div className={`rounded-lg border p-3 mb-3 ${inactiveAgentScan.total_mismatches > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="inactive-agent-scan-result">
                <div className="text-sm font-semibold mb-2">
                  {inactiveAgentScan.total_mismatches > 0
                    ? `${inactiveAgentScan.total_mismatches} mismatch(es) found — heal recommended`
                    : 'All assignments aligned ✓'}
                </div>
                {inactiveAgentScan.total_mismatches > 0 && (
                  <div className="max-h-64 overflow-auto">
                    <table className="text-xs w-full">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left p-1">Name</th>
                          <th className="text-left p-1">Email</th>
                          <th className="text-left p-1">Role</th>
                          <th className="text-left p-1">users.is_active</th>
                          <th className="text-left p-1">assignment.is_active</th>
                          <th className="text-left p-1">→ Will set to</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inactiveAgentScan.mismatches.map((m, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-1">{m.name || '-'}</td>
                            <td className="p-1"><code className="text-[10px]">{m.email || '-'}</code></td>
                            <td className="p-1">{m.role || '-'}</td>
                            <td className="p-1">{String(m.users_is_active)}</td>
                            <td className="p-1">{String(m.assignment_is_active)}</td>
                            <td className="p-1 font-semibold">{String(m.proposed_assignment_is_active)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {inactiveAgentHealResult && (
              <div className={`rounded-lg border p-3 ${inactiveAgentHealResult.dry_run ? 'bg-slate-50 border-slate-200' : 'bg-emerald-50 border-emerald-300'}`} data-testid="inactive-agent-heal-result">
                <div className="text-sm font-semibold">
                  {inactiveAgentHealResult.dry_run
                    ? `Dry run: would heal ${inactiveAgentHealResult.total_would_heal} row(s)`
                    : `Applied: healed ${inactiveAgentHealResult.total_healed} row(s)`}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Password Hash Drift Heal — May 18 — fixes recurring bcrypt_mismatch on invitation */}
        <Card className="mb-6 border-violet-200" data-testid="pw-drift-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-violet-600" />
              Password Hash Drift Heal
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Permanent fix for the recurring <code>bcrypt_mismatch</code> login failure after re-invite. Some users' password is stored in the legacy <code>password</code> field while login reads <code>password_hash</code> first — causing invitations to silently fail. Forward-fix (every invite path) is shipped; this tool consolidates existing prod rows to the canonical field. <strong>No user password changes</strong> — existing logins keep working.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3 flex-wrap">
              <Button
                data-testid="pw-drift-scan-btn"
                onClick={runPwDriftScan}
                disabled={pwDriftScanLoading}
                variant="outline"
              >
                {pwDriftScanLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Scan Drift
              </Button>
              <Button
                data-testid="pw-drift-dry-run-btn"
                onClick={() => runPwDriftHeal(true)}
                disabled={pwDriftHealLoading || !pwDriftScan || ((pwDriftScan.total_both_set + pwDriftScan.total_legacy_only) === 0)}
                variant="outline"
                className="border-violet-300 text-violet-700 hover:bg-violet-50"
              >
                {pwDriftHealLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Dry Run Heal
              </Button>
              <Button
                data-testid="pw-drift-apply-btn"
                onClick={() => runPwDriftHeal(false)}
                disabled={pwDriftHealLoading || !pwDriftScan || ((pwDriftScan.total_both_set + pwDriftScan.total_legacy_only) === 0)}
                className="bg-violet-600 hover:bg-violet-700"
              >
                Apply Heal
              </Button>
            </div>

            {pwDriftScan && (
              <div className="rounded-lg border bg-violet-50 border-violet-200 p-3 mb-3" data-testid="pw-drift-scan-result">
                <div className="text-sm font-semibold mb-2">
                  Found {pwDriftScan.total_both_set} both-set + {pwDriftScan.total_legacy_only} legacy-only = {(pwDriftScan.total_both_set + pwDriftScan.total_legacy_only)} row(s) to migrate
                </div>
                <div className="text-xs text-slate-600">
                  • <strong>both-set</strong>: keeps <code>password_hash</code>, drops legacy <code>password</code><br />
                  • <strong>legacy-only</strong>: migrates <code>password</code> → <code>password_hash</code>, then drops legacy
                </div>
              </div>
            )}

            {pwDriftHealResult && (
              <div className={`rounded-lg border p-3 ${pwDriftHealResult.dry_run ? 'bg-slate-50 border-slate-200' : 'bg-emerald-50 border-emerald-300'}`} data-testid="pw-drift-heal-result">
                <div className="text-sm font-semibold">
                  {pwDriftHealResult.dry_run
                    ? `Dry run: would migrate ${pwDriftHealResult.total_would_heal} row(s)`
                    : `Applied: migrated ${pwDriftHealResult.total_healed} row(s) to canonical password_hash`}
                </div>
              </div>
            )}
          </CardContent>
        </Card>









        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Production Data Warning</p>
              <p className="text-sm text-amber-700">Always run <strong>Preview</strong> first to see which cases will be affected. Migrations modify live data. Each migration has a <strong>Rollback</strong> option.</p>
            </div>
          </div>
        </div>

        {MIGRATIONS.map(migration => {
          const preview = previews[migration.id];
          const result = results[migration.id];

          return (
            <Card key={migration.id} className="mb-6" data-testid={`migration-card-${migration.id}`}>
              <CardHeader>
                <CardTitle className="text-lg">{migration.title}</CardTitle>
                <p className="text-sm text-gray-500 mt-1">{migration.description}</p>
                <p className="text-xs text-gray-400 font-mono mt-1">Filter: {migration.criteria}</p>
              </CardHeader>
              <CardContent>
                {migration.supportsPrefix && (
                  <div className="mb-3">
                    <label className="text-xs font-medium text-slate-600 mb-1 block">
                      Scope by Case ID prefix (optional)
                    </label>
                    <Input
                      type="text"
                      value={migrationPrefixes[migration.id] || ''}
                      onChange={(e) => setMigrationPrefixes(prev => ({ ...prev, [migration.id]: e.target.value }))}
                      placeholder={migration.prefixPlaceholder || 'e.g. INC210426_'}
                      data-testid={`case-prefix-input-${migration.id}`}
                      className="max-w-sm text-sm"
                    />
                  </div>
                )}
                {migration.id === 'heal-agent-assignment' && (
                  <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-sm font-medium text-slate-700 mb-2">
                      <Stethoscope className="inline w-4 h-4 mr-1 text-purple-600" />
                      Diagnose Agent Visibility
                    </p>
                    <p className="text-xs text-slate-500 mb-3">
                      Check whether a specific agent's dashboard should see their checks on the deployed backend. Compares OLD vs NEW filter behavior and flags any stuck records.
                    </p>
                    <div className="flex gap-2 flex-wrap items-end">
                      <div className="flex-1 min-w-[240px]">
                        <label className="text-xs text-slate-600 block mb-1">Agent Email</label>
                        <Input
                          type="email"
                          value={visEmail}
                          onChange={(e) => setVisEmail(e.target.value)}
                          placeholder="vendor-initiations@veecheck.com"
                          data-testid="vis-email-input"
                          className="text-sm"
                        />
                      </div>
                      <div className="flex-1 min-w-[180px]">
                        <label className="text-xs text-slate-600 block mb-1">Case Prefix (optional)</label>
                        <Input
                          type="text"
                          value={visPrefix}
                          onChange={(e) => setVisPrefix(e.target.value)}
                          placeholder="INC210426_"
                          data-testid="vis-prefix-input"
                          className="text-sm"
                        />
                      </div>
                      <Button
                        variant="outline"
                        onClick={runAgentVisibilityDiag}
                        disabled={visLoading}
                        data-testid="run-vis-diag-btn"
                        className="text-purple-700 border-purple-300 hover:bg-purple-50"
                      >
                        {visLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Stethoscope className="h-4 w-4 mr-2" />}
                        Run Diagnosis
                      </Button>
                    </div>
                    {visResult && (
                      <div className="mt-4 bg-white border border-slate-200 rounded p-3 text-xs" data-testid="vis-result">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3">
                          <div><span className="text-slate-500">User:</span> <span className="font-medium">{visResult.user?.name} ({visResult.user?.email})</span></div>
                          <div><span className="text-slate-500">Role:</span> {visResult.user?.role} {visResult.user?.vendor_id ? `· vendor=${visResult.user.vendor_id}` : ''} {visResult.user?.is_active === false ? <Badge variant="outline" className="text-xs text-red-600 border-red-300 ml-1">INACTIVE</Badge> : ''}</div>
                          <div><span className="text-slate-500">Scope:</span> {visResult.scope_prefix || 'all cases'}</div>
                          <div><span className="text-slate-500">Total checks scanned:</span> <span className="font-mono">{visResult.total_checks_scanned}</span></div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                          <div className="bg-slate-100 rounded p-2">
                            <div className="text-[11px] text-slate-500">Name matches</div>
                            <div className="text-lg font-mono font-semibold">{visResult.checks_where_name_matches_user}</div>
                          </div>
                          <div className="bg-orange-50 rounded p-2 border border-orange-200">
                            <div className="text-[11px] text-slate-500">OLD filter visible</div>
                            <div className="text-lg font-mono font-semibold text-orange-700">{visResult.visible_with_old_backend_filter}</div>
                          </div>
                          <div className="bg-green-50 rounded p-2 border border-green-200">
                            <div className="text-[11px] text-slate-500">NEW filter visible</div>
                            <div className="text-lg font-mono font-semibold text-green-700">{visResult.visible_with_new_backend_filter}</div>
                          </div>
                          <div className="bg-blue-50 rounded p-2 border border-blue-200">
                            <div className="text-[11px] text-slate-500">Restored by fix</div>
                            <div className="text-lg font-mono font-semibold text-blue-700">{visResult.restored_only_by_new_filter}</div>
                          </div>
                        </div>
                        {visResult.case_status_of_owned_checks && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mb-2 text-xs">
                            <p className="font-medium text-slate-700 mb-1">Case-status breakdown (ownership passed):</p>
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-0.5">
                              {Object.entries(visResult.case_status_of_owned_checks).map(([k, v]) => {
                                const allowed = ['basic_initiated','qc_approved','in_progress','completed','final_report_qc','report_approved'].includes(k);
                                return (
                                  <div key={k} className="flex justify-between border-b border-yellow-100 py-0.5">
                                    <span className={allowed ? 'text-slate-700' : 'text-red-700 font-medium'}>{k} {allowed ? '' : '✗'}</span>
                                    <span className="font-mono">{v}</span>
                                  </div>
                                );
                              })}
                            </div>
                            {visResult.dropped_by_case_status_filter > 0 && (
                              <p className="text-red-700 mt-1">
                                ⚠ {visResult.dropped_by_case_status_filter} check(s) dropped — case status outside the agent-allowed list.
                              </p>
                            )}
                          </div>
                        )}
                        <p className="text-[11px] text-slate-600 italic mb-2">{visResult.interpretation}</p>
                        {visResult.sample_name_matches_but_filter_misses?.length > 0 && (
                          <div className="bg-red-50 border border-red-200 rounded p-2 mt-2">
                            <p className="text-xs font-medium text-red-800 mb-1">
                              ⚠ Name matches but filter misses — these are stuck:
                            </p>
                            <div className="overflow-x-auto max-h-40 overflow-y-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Case</TableHead>
                                    <TableHead>Check</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>assigned_to</TableHead>
                                    <TableHead>agent_assigned_to</TableHead>
                                    <TableHead>vendor_id</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {visResult.sample_name_matches_but_filter_misses.map((c, i) => (
                                    <TableRow key={i}>
                                      <TableCell className="font-mono text-[11px]">{c.case_id}</TableCell>
                                      <TableCell className="text-[11px]">{c.check_type}</TableCell>
                                      <TableCell className="text-[11px]">{c.status}</TableCell>
                                      <TableCell className="text-[11px]">{c.assigned_to || '-'}</TableCell>
                                      <TableCell className="text-[11px]">{c.agent_assigned_to || '-'}</TableCell>
                                      <TableCell className="text-[11px]">{c.vendor_id || '-'}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-3 mb-4 flex-wrap items-center">
                  {migration.id === 'basic-initiated' && (
                    <label className="flex items-center gap-2 text-sm text-slate-700 bg-blue-50 px-3 py-2 rounded border border-blue-200" data-testid="basic-initiated-form-pending-toggle-wrap">
                      <input
                        type="checkbox"
                        checked={onlyFormPending}
                        onChange={(e) => setOnlyFormPending(e.target.checked)}
                        className="rounded border-blue-300"
                        data-testid="basic-initiated-form-pending-toggle"
                      />
                      Show only form-pending + in_progress
                    </label>
                  )}
                  {migration.id === 'basic-initiated' && (
                    <Button
                      variant="outline"
                      onClick={runDiagnose}
                      disabled={loading['diagnose']}
                      className="text-purple-600 border-purple-300 hover:bg-purple-50"
                      data-testid="diagnose-btn"
                    >
                      {loading['diagnose'] ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Stethoscope className="h-4 w-4 mr-2" />}
                      Diagnose
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => runPreview(migration.id)}
                    disabled={loading[migration.id + '-preview']}
                    data-testid={`preview-btn-${migration.id}`}
                  >
                    {loading[migration.id + '-preview'] ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                    Preview (Dry Run)
                  </Button>
                  <Button
                    onClick={() => runExecute(migration.id)}
                    disabled={loading[migration.id + '-execute']}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid={`execute-btn-${migration.id}`}
                  >
                    {loading[migration.id + '-execute'] ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                    Execute Migration
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => runRollback(migration.id)}
                    disabled={loading[migration.id + '-rollback'] || migration.id === 'heal-agent-assignment'}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    data-testid={`rollback-btn-${migration.id}`}
                    title={migration.id === 'heal-agent-assignment' ? 'Rollback not applicable — migration only adds preserved fields and is idempotent' : ''}
                  >
                    {loading[migration.id + '-rollback'] ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                    Rollback
                  </Button>
                </div>

                {/* Diagnostic Results for basic-initiated */}
                {migration.id === 'basic-initiated' && diagnostic && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4" data-testid="diagnostic-results">
                    <h3 className="font-semibold text-purple-800 mb-2">Diagnostic Results</h3>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-600">Basic First Clients:</p>
                        {diagnostic.basic_first_clients?.map(c => (
                          <Badge key={c.id} variant="secondary" className="mr-1 mb-1">{c.company_name} ({c.id})</Badge>
                        ))}
                        {(!diagnostic.basic_first_clients || diagnostic.basic_first_clients.length === 0) && (
                          <p className="text-sm text-red-600 font-medium">None found! No clients have pre_employment_basic_first enabled.</p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">All Cases by Status (for these clients):</p>
                        {diagnostic.all_status_breakdown && Object.entries(diagnostic.all_status_breakdown).map(([status, count]) => (
                          <Badge key={status} variant="outline" className="mr-1 mb-1">{status}: {count}</Badge>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm font-medium text-purple-700 mb-1">
                      Eligible for Migration: {diagnostic.eligible_for_migration} cases
                      {diagnostic.will_skip > 0 && (
                        <span className="text-gray-500 ml-2">| Will Skip: {diagnostic.will_skip} (genuine in_progress)</span>
                      )}
                    </p>
                    {diagnostic.eligible_by_status && (
                      <div className="flex gap-2 mb-2">
                        {Object.entries(diagnostic.eligible_by_status).map(([status, count]) => (
                          <Badge key={status} className="bg-purple-100 text-purple-800">{status}: {count}</Badge>
                        ))}
                      </div>
                    )}
                    {diagnostic.cases_sample?.length > 0 && (
                      <div className="mt-2 overflow-x-auto max-h-48 overflow-y-auto border rounded">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10">
                                <Checkbox
                                  checked={diagnostic.cases_sample.filter(c => !c.migration_action?.includes('SKIP')).length > 0 && diagnostic.cases_sample.filter(c => !c.migration_action?.includes('SKIP')).every(c => selectedMigrationCases.has(c.id))}
                                  onCheckedChange={(checked) => {
                                    const migratable = diagnostic.cases_sample.filter(c => !c.migration_action?.includes('SKIP')).map(c => c.id);
                                    if (checked) {
                                      setSelectedMigrationCases(new Set([...selectedMigrationCases, ...migratable]));
                                    } else {
                                      const next = new Set(selectedMigrationCases);
                                      migratable.forEach(id => next.delete(id));
                                      setSelectedMigrationCases(next);
                                    }
                                  }}
                                />
                              </TableHead>
                              <TableHead>Case ID</TableHead>
                              <TableHead>Name</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Action</TableHead>
                              <TableHead>basic_promoted_to_qc</TableHead>
                              <TableHead>basic_only_package</TableHead>
                              <TableHead>employee_submitted</TableHead>
                              <TableHead>Source</TableHead>
                              <TableHead>Created</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {diagnostic.cases_sample.map((c, i) => {
                              const isMigrate = !c.migration_action?.includes('SKIP');
                              return (
                              <TableRow key={i} className={selectedMigrationCases.has(c.id) ? 'bg-blue-50' : ''}>
                                <TableCell>
                                  {isMigrate && (
                                    <Checkbox
                                      checked={selectedMigrationCases.has(c.id)}
                                      onCheckedChange={(checked) => {
                                        const next = new Set(selectedMigrationCases);
                                        if (checked) next.add(c.id); else next.delete(c.id);
                                        setSelectedMigrationCases(next);
                                      }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell className="font-mono text-xs">{c.id}</TableCell>
                                <TableCell className="text-xs">{c.employee_name || '-'}</TableCell>
                                <TableCell><Badge variant="outline" className="text-xs">{c.status}</Badge></TableCell>
                                <TableCell><Badge className={`text-xs ${!isMigrate ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-800'}`}>{!isMigrate ? 'SKIP' : 'MIGRATE'}</Badge></TableCell>
                                <TableCell className="text-xs">{String(c.basic_promoted_to_qc ?? 'not set')}</TableCell>
                                <TableCell className="text-xs">{String(c.basic_only_package ?? 'not set')}</TableCell>
                                <TableCell className="text-xs">{String(c.employee_submitted ?? 'not set')}</TableCell>
                                <TableCell className="text-xs">{c.source || '-'}</TableCell>
                                <TableCell className="text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB') : '-'}</TableCell>
                              </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                    {selectedMigrationCases.size > 0 && (
                      <div className="mt-3 flex items-center gap-3">
                        <Button
                          onClick={() => runExecute('basic-initiated', Array.from(selectedMigrationCases))}
                          disabled={loading['basic-initiated-execute']}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="migrate-selected-btn"
                        >
                          {loading['basic-initiated-execute'] ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                          Migrate Selected ({selectedMigrationCases.size})
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedMigrationCases(new Set())} className="text-gray-500">
                          Clear Selection
                        </Button>
                      </div>
                    )}
                    {diagnostic.skipped_cases?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1 font-medium">Skipped Cases (genuine in_progress — all checks active):</p>
                        <div className="overflow-x-auto max-h-32 overflow-y-auto border border-gray-200 rounded">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Case ID</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Action</TableHead>
                                <TableHead>Source</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {diagnostic.skipped_cases.map((c, i) => (
                                <TableRow key={i} className="bg-gray-50">
                                  <TableCell className="font-mono text-xs">{c.id}</TableCell>
                                  <TableCell className="text-xs">{c.employee_name || '-'}</TableCell>
                                  <TableCell><Badge variant="outline" className="text-xs">{c.status}</Badge></TableCell>
                                  <TableCell><Badge className="bg-gray-200 text-gray-700 text-xs">SKIP</Badge></TableCell>
                                  <TableCell className="text-xs">{c.source || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {result && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4" data-testid={`result-${migration.id}`}>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800">{result.message}</span>
                    </div>
                    {result.linked_details?.length > 0 && (
                      <div className="mt-2 overflow-x-auto max-h-40 overflow-y-auto border rounded">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Employee</TableHead>
                              <TableHead>Case ID Linked</TableHead>
                              <TableHead>Case Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {result.linked_details.map((d, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-xs">{d.name} ({d.employee_id})</TableCell>
                                <TableCell className="font-mono text-xs">{d.case_id}</TableCell>
                                <TableCell><Badge variant="outline" className="text-xs">{d.case_status}</Badge></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                    {result.skipped_ids?.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">Skipped (genuine in_progress): {result.skipped_ids.join(', ')}</p>
                    )}
                  </div>
                )}

                {preview && migration.id === 'heal-agent-assignment' && (
                  <div data-testid="heal-agent-preview" className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                        {preview.count || 0} checks will be healed
                      </Badge>
                      <Badge variant="outline" className="text-xs">Scanned cases: {preview.scanned_cases}</Badge>
                      <Badge variant="outline" className="text-xs">Checks examined: {preview.scanned_checks}</Badge>
                      {preview.skipped_already_healed > 0 && (
                        <Badge variant="outline" className="text-xs text-green-700">Already healed: {preview.skipped_already_healed}</Badge>
                      )}
                      {preview.skipped_no_vendor_on_check > 0 && (
                        <Badge variant="outline" className="text-xs">No vendor on check: {preview.skipped_no_vendor_on_check}</Badge>
                      )}
                      {preview.skipped_name_collision > 0 && (
                        <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">Name collisions: {preview.skipped_name_collision}</Badge>
                      )}
                      <span className="text-xs text-gray-400">{preview.timestamp}</span>
                    </div>
                    {preview.no_vendor_by_name?.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs">
                        <p className="font-medium text-blue-900 mb-1">
                          ℹ {preview.skipped_no_vendor_on_check} checks don't need healing — no vendor attached. They're already visible to the agent via direct assignment. Breakdown by agent name:
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 mt-2">
                          {preview.no_vendor_by_name.slice(0, 12).map((row, i) => (
                            <div key={i} className="flex justify-between border-b border-blue-100 py-0.5">
                              <span className="text-slate-700">{row.name}</span>
                              <span className="font-mono text-slate-500">{row.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {preview.sample_heals?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-600 mb-1">Sample (first {preview.sample_heals.length} of {preview.count} heals):</p>
                        <div className="overflow-x-auto max-h-64 overflow-y-auto border rounded">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Case ID</TableHead>
                                <TableHead>Check</TableHead>
                                <TableHead>Vendor</TableHead>
                                <TableHead>Original Agent</TableHead>
                                <TableHead>Agent Email</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {preview.sample_heals.map((h, i) => (
                                <TableRow key={i}>
                                  <TableCell className="font-mono text-xs">{h.case_id}</TableCell>
                                  <TableCell className="text-xs">{h.check_type}{h.sub_option ? ` / ${h.sub_option}` : ''}</TableCell>
                                  <TableCell className="text-xs">{h.vendor_name || '-'}</TableCell>
                                  <TableCell className="text-xs font-medium">{h.agent_name}</TableCell>
                                  <TableCell className="text-xs text-slate-500">{h.agent_email}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                    {preview.collisions?.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded p-3">
                        <p className="text-xs font-medium text-amber-800 mb-2">
                          <AlertTriangle className="inline w-3 h-3 mr-1" />
                          {preview.skipped_name_collision} check(s) skipped due to ambiguous agent name. Showing first {preview.collisions.length}:
                        </p>
                        <div className="overflow-x-auto max-h-40 overflow-y-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Case</TableHead>
                                <TableHead>Check</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Candidates</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {preview.collisions.map((col, i) => (
                                <TableRow key={i}>
                                  <TableCell className="font-mono text-xs">{col.case_id}</TableCell>
                                  <TableCell className="text-xs">{col.check_type}</TableCell>
                                  <TableCell className="text-xs">{col.assigned_to_name}</TableCell>
                                  <TableCell className="text-xs">
                                    {col.matches.map((m, j) => (
                                      <div key={j} className="text-[11px]">{m.email} <span className="text-slate-400">({m.role})</span></div>
                                    ))}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                    {preview.count === 0 && preview.skipped_name_collision === 0 && (
                      <p className="text-xs text-slate-500">No eligible checks found for healing. All clean!</p>
                    )}
                  </div>
                )}

                {preview && preview.count > 0 && migration.id !== 'link-case-ids' && migration.id !== 'heal-agent-assignment' && (
                  <div data-testid={`preview-table-${migration.id}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge variant="secondary">{preview.count} cases will be migrated</Badge>
                      {preview.by_status && Object.entries(preview.by_status).map(([status, count]) => (
                        <Badge key={status} variant="outline" className="text-xs">{status}: {count}</Badge>
                      ))}
                      <span className="text-xs text-gray-400">{preview.timestamp}</span>
                    </div>
                    <div className="overflow-x-auto max-h-64 overflow-y-auto border rounded">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Case ID</TableHead>
                            <TableHead>Employee Name</TableHead>
                            <TableHead>Current Status</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Created</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {preview.cases.map((c, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-mono text-xs">{c.id}</TableCell>
                              <TableCell>{c.employee_name || '-'}</TableCell>
                              <TableCell><Badge variant="outline" className="text-xs">{c.status}</Badge></TableCell>
                              <TableCell>{c.employee_email || '-'}</TableCell>
                              <TableCell>{c.created_at ? c.created_at.substring(0, 10) : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Link Case IDs preview */}
                {preview && migration.id === 'link-case-ids' && (
                  <div data-testid="preview-link-case-ids">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge variant="secondary" className="bg-green-100 text-green-800">{preview.linkable || 0} can be linked</Badge>
                      <Badge variant="secondary" className="bg-red-100 text-red-800">{preview.orphaned || 0} orphaned (no case)</Badge>
                      <span className="text-xs text-gray-400">{preview.timestamp}</span>
                    </div>
                    {preview.linkable_cases?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-green-700 mb-1">Will Link ({preview.linkable}):</p>
                        <div className="overflow-x-auto max-h-48 overflow-y-auto border rounded">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Employee</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Entity</TableHead>
                                <TableHead>Case ID</TableHead>
                                <TableHead>Case Status</TableHead>
                                <TableHead>Details Filled</TableHead>
                                <TableHead>Declaration</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {preview.linkable_cases.map((c, i) => (
                                <TableRow key={i}>
                                  <TableCell className="font-mono text-xs">{c.employee_id}</TableCell>
                                  <TableCell className="text-xs">{c.name || '-'}</TableCell>
                                  <TableCell className="text-xs">{c.entity_name || '-'}</TableCell>
                                  <TableCell className="font-mono text-xs">{c.case_id}</TableCell>
                                  <TableCell><Badge variant="outline" className="text-xs">{c.case_status}</Badge></TableCell>
                                  <TableCell className="text-xs">{String(c.details_filled || false)}</TableCell>
                                  <TableCell className="text-xs">{String(c.declaration_signed || false)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                    {preview.orphaned_employees?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-red-700 mb-1">Orphaned — No Case Found ({preview.orphaned}):</p>
                        <div className="overflow-x-auto max-h-32 overflow-y-auto border border-red-200 rounded">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Employee</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Entity</TableHead>
                                <TableHead>Email</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {preview.orphaned_employees.map((c, i) => (
                                <TableRow key={i} className="bg-red-50">
                                  <TableCell className="font-mono text-xs">{c.employee_id}</TableCell>
                                  <TableCell className="text-xs">{c.name || '-'}</TableCell>
                                  <TableCell className="text-xs">{c.entity_name || '-'}</TableCell>
                                  <TableCell className="text-xs">{c.email || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                    {(preview.linkable === 0 && preview.orphaned === 0) && (
                      <div className="text-sm text-gray-500 italic">All employees have case IDs linked. Nothing to fix.</div>
                    )}
                  </div>
                )}

                {preview && preview.count === 0 && migration.id !== 'link-case-ids' && migration.id !== 'clean-orphaned-employees' && migration.id !== 'heal-agent-assignment' && (
                  <div className="text-sm text-gray-500 italic" data-testid={`no-cases-${migration.id}`}>
                    No cases match the criteria. Nothing to migrate.
                  </div>
                )}

                {/* Clean Orphaned Employees preview */}
                {preview && migration.id === 'clean-orphaned-employees' && (
                  <CleanOrphanedPreview
                    preview={preview}
                    entityFilter={entityFilter}
                    setEntityFilter={setEntityFilter}
                    selectedOrphans={selectedOrphans}
                    setSelectedOrphans={setSelectedOrphans}
                    runDeleteSelected={runDeleteSelected}
                    loading={loading}
                  />
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* Performance / Indexes Card */}
        <Card className="border-2 border-purple-200" data-testid="perf-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Database className="h-5 w-5 text-purple-600" />
              Database Performance & Indexes
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Profile the heavy list queries (All Cases / Check-wise / Agent Vendor Team) and apply recommended indexes. No data changes — index creation only.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 flex-wrap mb-3">
              <Button
                variant="outline"
                onClick={runQueryProfile}
                disabled={perfLoading}
                data-testid="run-query-profile-btn"
                className="text-purple-700 border-purple-300 hover:bg-purple-50"
              >
                {perfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Stethoscope className="h-4 w-4 mr-2" />}
                Run Query Profile
              </Button>
              <Button
                variant="outline"
                onClick={applyRecommendedIndexes}
                disabled={perfApplyLoading || !perfResult}
                data-testid="apply-indexes-btn"
                className="text-green-700 border-green-300 hover:bg-green-50"
              >
                {perfApplyLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
                Apply Recommended Indexes
              </Button>
            </div>
            {perfResult && (
              <div className="space-y-3" data-testid="perf-result">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <Badge variant="outline">Cases: {perfResult.totals?.verification_cases}</Badge>
                  <Badge variant="outline">Employees: {perfResult.totals?.employees}</Badge>
                  <Badge variant="outline">Insufficiencies: {perfResult.totals?.insufficiencies}</Badge>
                  <Badge variant="outline">Users: {perfResult.totals?.users}</Badge>
                  <span className="text-gray-400 ml-2">{perfResult.generated_at}</span>
                </div>
                <div className="overflow-x-auto border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Query</TableHead>
                        <TableHead className="text-right">Time (ms)</TableHead>
                        <TableHead className="text-right">Rows</TableHead>
                        <TableHead>Index Used</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {perfResult.profiled_queries?.map((q, i) => {
                        const slow = q.ms > 200;
                        const collscan = (q.index_used || '').includes('COLLSCAN');
                        return (
                          <TableRow key={i}>
                            <TableCell className="text-xs">{q.name}</TableCell>
                            <TableCell className={`text-right text-xs font-mono ${slow ? 'text-red-700 font-semibold' : 'text-green-700'}`}>{q.ms}</TableCell>
                            <TableCell className="text-right text-xs font-mono">{q.rows}</TableCell>
                            <TableCell className={`text-xs ${collscan ? 'text-red-700 font-medium' : 'text-slate-600'}`}>{q.index_used}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                {perfResult.recommended_indexes_to_add?.length > 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs">
                    <p className="font-medium text-amber-900 mb-2">Recommended new indexes ({perfResult.recommended_indexes_to_add.length}):</p>
                    <ul className="space-y-1">
                      {perfResult.recommended_indexes_to_add.map((rec, i) => (
                        <li key={i} className="flex justify-between border-b border-amber-100 py-0.5">
                          <span className="font-mono text-slate-700">{JSON.stringify(rec.key)}</span>
                          <span className="text-slate-500">{rec.why}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-[11px] italic text-slate-500 mt-2">Click "Apply Recommended Indexes" to create them. Idempotent and safe.</p>
                  </div>
                ) : (
                  <p className="text-xs text-green-700 italic">All recommended indexes are already in place.</p>
                )}
              </div>
            )}
            {perfApplyResult && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded p-3 text-xs">
                <p className="font-medium text-green-900">{perfApplyResult.message}</p>
                {perfApplyResult.created?.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {perfApplyResult.created.map((c, i) => (
                      <li key={i} className="font-mono text-slate-700">+ {c.collection}: {c.name}</li>
                    ))}
                  </ul>
                )}
                {perfApplyResult.errors?.length > 0 && (
                  <div className="mt-2 text-red-700">
                    <p className="font-medium">Errors:</p>
                    {perfApplyResult.errors.map((e, i) => (
                      <p key={i} className="font-mono">{e.collection}: {e.error}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function CleanOrphanedPreview({ preview, entityFilter, setEntityFilter, selectedOrphans, setSelectedOrphans, runDeleteSelected, loading }) {
  const allOrphans = preview.orphaned_employees || [];

  // Get unique entity names
  const entities = useMemo(() => {
    const map = {};
    allOrphans.forEach(e => {
      const name = e.entity_name || 'Unknown';
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [allOrphans]);

  // Filter by selected entity
  const filteredOrphans = entityFilter === 'all'
    ? allOrphans
    : allOrphans.filter(e => (e.entity_name || 'Unknown') === entityFilter);

  const toggleOne = (id) => {
    setSelectedOrphans(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const visibleIds = filteredOrphans.map(e => e.id);
    const allSelected = visibleIds.every(id => selectedOrphans.has(id));
    setSelectedOrphans(prev => {
      const next = new Set(prev);
      visibleIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const visibleSelected = filteredOrphans.filter(e => selectedOrphans.has(e.id)).length;

  return (
    <div data-testid="preview-clean-orphaned">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Badge variant="secondary" className="bg-red-100 text-red-800">{preview.truly_orphaned || 0} orphaned total</Badge>
        {preview.has_case_unlinked > 0 && (
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">{preview.has_case_unlinked} have case — safe</Badge>
        )}
        {selectedOrphans.size > 0 && (
          <Badge variant="secondary" className="bg-blue-100 text-blue-800">{selectedOrphans.size} selected for deletion</Badge>
        )}
        <span className="text-xs text-gray-400">{preview.timestamp}</span>
      </div>

      {/* Entity filter badges */}
      {entities.length > 1 && (
        <div className="flex gap-1.5 mb-3 flex-wrap items-center">
          <span className="text-xs font-medium text-gray-500 mr-1">Filter by entity:</span>
          <Badge
            data-testid="entity-filter-all"
            variant={entityFilter === 'all' ? 'default' : 'outline'}
            className={`text-xs cursor-pointer ${entityFilter === 'all' ? 'bg-slate-800' : 'hover:bg-slate-100'}`}
            onClick={() => setEntityFilter('all')}
          >
            All ({allOrphans.length})
          </Badge>
          {entities.map(([name, count]) => (
            <Badge
              key={name}
              data-testid={`entity-filter-${name.replace(/\s+/g, '-')}`}
              variant={entityFilter === name ? 'default' : 'outline'}
              className={`text-xs cursor-pointer ${entityFilter === name ? 'bg-red-700' : 'hover:bg-red-50 text-red-700 border-red-300'}`}
              onClick={() => setEntityFilter(entityFilter === name ? 'all' : name)}
            >
              {name} ({count})
            </Badge>
          ))}
        </div>
      )}

      {preview.warning && (
        <div className="bg-red-50 border border-red-200 rounded p-2 mb-2 text-xs text-red-800 font-medium">{preview.warning}</div>
      )}

      {filteredOrphans.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-red-700">
              Showing {filteredOrphans.length} orphaned {entityFilter !== 'all' ? `in "${entityFilter}"` : 'across all entities'}
              {visibleSelected > 0 ? ` (${visibleSelected} selected)` : ''}:
            </p>
            {selectedOrphans.size > 0 && (
              <Button
                data-testid="delete-selected-btn"
                size="sm"
                variant="destructive"
                onClick={runDeleteSelected}
                disabled={loading['clean-orphaned-employees-execute']}
                className="h-7 text-xs"
              >
                {loading['clean-orphaned-employees-execute'] ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Trash2 className="w-3 h-3 mr-1" />
                )}
                Delete {selectedOrphans.size} Selected
              </Button>
            )}
          </div>
          <div className="overflow-x-auto max-h-80 overflow-y-auto border border-red-200 rounded">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      data-testid="select-all-checkbox"
                      checked={filteredOrphans.length > 0 && filteredOrphans.every(e => selectedOrphans.has(e.id))}
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Stored Case ID</TableHead>
                  <TableHead>Has Login</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Declaration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrphans.map((c) => (
                  <TableRow key={c.id} className={selectedOrphans.has(c.id) ? 'bg-red-100' : 'bg-red-50'}>
                    <TableCell>
                      <Checkbox
                        data-testid={`select-orphan-${c.id}`}
                        checked={selectedOrphans.has(c.id)}
                        onCheckedChange={() => toggleOne(c.id)}
                      />
                    </TableCell>
                    <TableCell className="text-xs font-medium">{c.name || '-'}</TableCell>
                    <TableCell className="text-xs">{c.entity_name || '-'}</TableCell>
                    <TableCell className="text-xs">{c.email || '-'}</TableCell>
                    <TableCell className="font-mono text-xs text-red-600">{c.original_case_id || '(none)'}</TableCell>
                    <TableCell className="text-xs">{c.has_login ? 'Yes' : 'No'}</TableCell>
                    <TableCell className="text-xs">{String(c.details_filled || false)}</TableCell>
                    <TableCell className="text-xs">{String(c.declaration_signed || false)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {preview.unlinked_employees?.length > 0 && (
        <div>
          <p className="text-xs font-medium text-yellow-700 mb-1">Has Case (will NOT delete — use Link Case IDs):</p>
          <div className="overflow-x-auto max-h-32 overflow-y-auto border border-yellow-200 rounded">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Case ID</TableHead>
                  <TableHead>Case Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.unlinked_employees.map((c, i) => (
                  <TableRow key={i} className="bg-yellow-50">
                    <TableCell className="text-xs">{c.name || '-'}</TableCell>
                    <TableCell className="font-mono text-xs">{c.case_id}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{c.case_status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {(preview.truly_orphaned === 0 && preview.has_case_unlinked === 0) && (
        <div className="text-sm text-gray-500 italic">No orphaned employees found. All good.</div>
      )}
    </div>
  );
}
